import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Paper,
  Toolbar,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import {
  FormatAlignLeft as FormatIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,

  Router as RouterIcon,
  Light as LightIcon,
  Videocam as VideocamIcon,
  Link as LinkIcon,
  Security as AccessListIcon,
  Tune as ConfigureIcon,
  Send as CommandIcon,
  Monitor as RttyIcon,
  Download as DownloadIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import {
  getParentLatencyMs,
  getCapLatencyMs,
  getLatencyValue,
  getLatencyChipColor,
  getLatencyLabel,
  formatLatencyMs,
  formatLatencyTooltip,
} from '../services/latency';
import { auth } from '../services/auth';
import config from '../config';
import Editor from '@monaco-editor/react';
import LastKnownGoodMenu from './LastKnownGoodMenu';
import { isLastKnownGoodEligible } from '../utils/lkgUtils';
import PortForwardingSection from './PortForwardingSection';
import SyslogSection from './SyslogSection';
import DeviceReportMenuButton from './DeviceReportMenuButton';
import { fieldSectionSx } from './deviceConfigStyles';

// =============================================================================
// Channel-group ID catalogue (treemgmt PATCH/POST)
// =============================================================================
//
// `channel_group_ids` on a treemgmt entry is a list of pre-defined regulatory
// U-NII sub-bands. The backend expands the IDs into the equivalent
// `channel_groups` map per band at configure time, and resets the fixed
// `channel` to 0 (auto). DFS sub-bands (U-NII-2, U-NII-4) are intentionally
// not exposed.
//
// Keep this list in sync with openapi.yaml's PATCH/POST /tree-mgmt/* docs.
const UNII_GROUPS = [
  { id: 1, label: 'U-NII-1',  band: '5GHz', channels: '36 – 48' },
  { id: 2, label: 'U-NII-3',  band: '5GHz', channels: '149 – 165' },
  { id: 3, label: 'U-NII-5',  band: '6GHz', channels: '1 – 93' },
  { id: 4, label: 'U-NII-6',  band: '6GHz', channels: '97 – 113' },
  { id: 5, label: 'U-NII-7',  band: '6GHz', channels: '117 – 185' },
  { id: 6, label: 'U-NII-8',  band: '6GHz', channels: '189 – 221' },
];
const UNII_BY_ID = UNII_GROUPS.reduce((acc, g) => { acc[g.id] = g; return acc; }, {});

// Returns "1 — U-NII-1 (5 GHz, ch 36 – 48)" for a known id; falls back to the
// raw id for unknown values.
const formatUniiOption = (g) => `${g.id} — ${g.label} (${g.band === '5GHz' ? '5 GHz' : '6 GHz'}, ch ${g.channels})`;

// Builds a one-line per-band summary, e.g.:
//   "Auto channel pick across 5 GHz: ch 36 – 48; 6 GHz: ch 1 – 93, 97 – 113"
const summarizeUniiSelection = (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return '';
  const buckets = { '5GHz': [], '6GHz': [] };
  ids.forEach((id) => {
    const g = UNII_BY_ID[id];
    if (g) buckets[g.band].push(g.channels);
  });
  const parts = [];
  if (buckets['5GHz'].length) parts.push(`5 GHz: ch ${buckets['5GHz'].join(', ')}`);
  if (buckets['6GHz'].length) parts.push(`6 GHz: ch ${buckets['6GHz'].join(', ')}`);
  return parts.length ? `Auto channel pick across ${parts.join('; ')}` : '';
};

// Unused VLAN sub-interfaces (e.g. ath1.14) report mode ""; hide from Radio Interfaces UI.
const filterRadioInterfacesByMode = (interfaces) => {
  if (!interfaces || !Array.isArray(interfaces)) return [];
  return interfaces.filter((iface) => {
    const mode = iface?.Mode ?? iface?.mode;
    return String(mode ?? '').trim() !== '';
  });
};

const normalizeStationKey = (raw) => {
  if (raw == null || raw === '') return '';
  return String(raw).replace(/:/g, '').toLowerCase();
};

const isAPModeIface = (iface) => {
  const mode = String(iface?.Mode ?? iface?.mode ?? 'ap').toLowerCase();
  return mode === 'ap';
};

const collectAssociationStationKeys = (ifaces) => {
  const keys = new Set();
  (ifaces || []).forEach((iface) => {
    if (!isAPModeIface(iface)) return;
    (iface.Associations || iface.associations || []).forEach((assoc) => {
      const key = normalizeStationKey(assoc?.Station ?? assoc?.station);
      if (key) keys.add(key);
    });
  });
  return keys;
};

const deviceAssociationMatchKeys = (d) => {
  const keys = [];
  if (d?.serial_number) {
    const serial = d.serial_number.toLowerCase();
    keys.push(serial, serial.replace(/:/g, ''));
  }
  (d?.wifi_interfaces || []).forEach((w) => {
    const mac = normalizeStationKey(w?.macaddr ?? w?.mac);
    if (mac) keys.push(mac);
  });
  (d?.radios || []).forEach((r) => {
    const mac = normalizeStationKey(r?.mac);
    if (mac) keys.push(mac);
  });
  const privIfaces = d?.private_wifi?.interfaces || d?.private_wifi?.Interfaces || [];
  const stationIface = privIfaces.find(
    (iface) => String(iface?.Mode ?? iface?.mode ?? '').toLowerCase() === 'station',
  );
  const stationMac = normalizeStationKey(stationIface?.MAC ?? stationIface?.mac);
  if (stationMac) keys.push(stationMac);
  return keys;
};

const deviceMatchesAssociationStations = (d, stationKeys) => {
  if (!stationKeys?.size) return false;
  return deviceAssociationMatchKeys(d).some((k) => stationKeys.has(k));
};

const mergeDevicesBySerial = (...lists) => {
  const map = new Map();
  lists.flat().forEach((d) => {
    if (!d?.serial_number) return;
    const key = d.serial_number.toLowerCase();
    if (!map.has(key)) map.set(key, d);
  });
  return Array.from(map.values());
};

const countActiveAssociations = (ifaces) => (ifaces || [])
  .filter(isAPModeIface)
  .reduce((total, iface) => total + (iface.Associations || iface.associations || []).length, 0);

// Public WiFi client count from GET /device. -1 means that band is inactive for this interface.
const publicWifiClientCountForInterface = (iface, publicWifi, band) => {
  const count24 = iface?.num_clients_24g ?? publicWifi?.num_clients_24g;
  const count5 = iface?.num_clients_5g ?? publicWifi?.num_clients_5g;
  const active24 = count24 != null && count24 !== -1;
  const active5 = count5 != null && count5 !== -1;

  if (active24 && !active5) return Math.max(0, count24);
  if (active5 && !active24) return Math.max(0, count5);
  if (!active24 && !active5) return 0;

  const bandLower = String(band || '').toLowerCase();
  if (bandLower.includes('2.4')) return Math.max(0, count24);
  if (bandLower.includes('5') || bandLower.includes('6')) return Math.max(0, count5);
  return Math.max(0, count24, count5);
};

const BG_SELF_IMPROVEMENT_ALWAYS = 2147483647;

const isBgSelfImprovementAlways = (value) => {
  if (value === '' || value === undefined || value === null) return false;
  if (String(value).toLowerCase() === 'always') return true;
  return Number(value) === BG_SELF_IMPROVEMENT_ALWAYS;
};

const formatBgSelfImprovementDisplay = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  if (isBgSelfImprovementAlways(value)) return 'Always';
  return typeof value === 'number' ? String(value) : value;
};

const normalizeBgSelfImprovementInput = (value) => {
  const trimmed = String(value).trim();
  if (trimmed.toLowerCase() === 'always') {
    return String(BG_SELF_IMPROVEMENT_ALWAYS);
  }
  return trimmed;
};

const FieldLabelTip = ({ title, children }) => (
  <Tooltip title={title} enterDelay={400}>
    <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>
      {children}
    </Box>
  </Tooltip>
);

const bgScanSelectRenderValue = (selected) => selected || '\u00a0';

const DeviceDetails = ({ devices: devicesProp = [], onLogout, canWriteBackhaul = false }) => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Local state for devices - use prop if available, otherwise fetch on refresh
  const [localDevices, setLocalDevices] = useState([]);
  const fetchAttemptedRef = useRef(false);
  
  // Use prop devices if available, otherwise use locally fetched devices
  const devices = devicesProp.length > 0 ? devicesProp : localDevices;
  
  // Fetch devices only if prop is empty (page refresh scenario)
  useEffect(() => {
    const fetchDevicesForChildren = async () => {
      // Only fetch if we don't have devices from props and haven't fetched yet
      if (devicesProp.length > 0 || fetchAttemptedRef.current) return;
      
      // Mark as attempted immediately to prevent duplicate calls
      fetchAttemptedRef.current = true;
      
      try {
        const response = await api.getDevices({
          fields: ['core', 'radios', 'wifi_interfaces'],
        });
        // Handle new response format
        const devicesData = response?.devices || (Array.isArray(response) ? response : []);
        setLocalDevices(devicesData);
      } catch (err) {
        console.error('Error fetching devices for children display:', err);
        // Don't set error state - device details should still work without children
      }
    };
    
    fetchDevicesForChildren();
  }, [devicesProp.length]);

  // Add effect to handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      // When browser back button is pressed, go to device list with preserved filters
      const searchParams = new URLSearchParams(location.search);
      const queryString = searchParams.toString();
      navigate(`/devices${queryString ? `?${queryString}` : ''}`, { replace: true });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, location.search]);

  const handleBack = () => {
    // Preserve filter state from the URL when navigating back
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    navigate(`/devices${queryString ? `?${queryString}` : ''}`, { replace: true });
  };

  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configJson, setConfigJson] = useState('');
  const [originalConfig, setOriginalConfig] = useState('');
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [lkgStatus, setLkgStatus] = useState({ type: '', message: '' });
  const [isJsonValid, setIsJsonValid] = useState(true);

  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [accessListDialogOpen, setAccessListDialogOpen] = useState(false);
  const [selectedWhitelist, setSelectedWhitelist] = useState([]);
  const [selectedBlacklist, setSelectedBlacklist] = useState([]);
  const [parentUpdateStatus, setParentUpdateStatus] = useState({ type: '', message: '' });
  const [accessListUpdateStatus, setAccessListUpdateStatus] = useState({ type: '', message: '' });
  const [accessListInsistSec, setAccessListInsistSec] = useState('');
  const [whitelistDeviceFilter, setWhitelistDeviceFilter] = useState('');
  const [blacklistDeviceFilter, setBlacklistDeviceFilter] = useState('');
  const [parentDeviceFilter, setParentDeviceFilter] = useState('');
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [commandDialogOpen, setCommandDialogOpen] = useState(false);
  const [command, setCommand] = useState('');
  const [commandStatus, setCommandStatus] = useState({ type: '', message: '' });
  const [childAIDevices, setChildAIDevices] = useState([]);
  const [parentDevice, setParentDevice] = useState(null);
  const [childJnet2Devices, setChildJnet2Devices] = useState([]);
  const [childEdgeCoreDevices, setChildEdgeCoreDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [configMode, setConfigMode] = useState('fields');
  const [treeMgmtView, setTreeMgmtView] = useState(null);
  const [treeMgmtLoading, setTreeMgmtLoading] = useState(false);
  const [treeMgmtError, setTreeMgmtError] = useState(null);
  const [treeMgmtEdits, setTreeMgmtEdits] = useState({});
const [userInfo, setUserInfo] = useState(null);
const [environment, setEnvironment] = useState('');

  useEffect(() => {
    const info = auth.getUserInfo();
    setUserInfo(info);
    setEnvironment(config.getEnvironment());
  }, []);

  // Reset config state when serial changes (navigating to different device)
  useEffect(() => {
    setConfigJson('');
    setOriginalConfig('');
    setConfigError(null);
    setConfigMode('fields');
    setTreeMgmtView(null);
    setTreeMgmtError(null);
    setTreeMgmtEdits({});
  }, [serial]);

  const handleLogoutClick = useCallback(() => {
    if (typeof onLogout === 'function') {
      onLogout();
    } else {
      auth.logout();
      window.location.assign('/');
    }
  }, [onLogout]);

  const getPeerAddress = (address) => {
    if (!address) return '';
    // Remove port number if present
    return address.split(':')[0];
  };

  const fetchDeviceData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching device data for serial:', serial);
      const deviceData = await api.getDevice(serial);
      console.log('Device data received:', deviceData);
      setDevice(deviceData);
      setError(null);
    } catch (err) {
      console.error('Error fetching device data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch device data');
    } finally {
      setLoading(false);
    }
  }, [serial]);

  useEffect(() => {
    console.log('DeviceDetails mounted with serial:', serial);
    fetchDeviceData();
  }, [serial, fetchDeviceData]);

  // Resolve AI device: prefer discovered AI children, then matching device by ai_device_mac, then fallback chip
  const resolvedAIDevices = useMemo(() => {
    const mac = device?.ai_device_mac?.toLowerCase();
    if (!mac) return childAIDevices;

    // If we already have AI children detected, use them
    if (childAIDevices && childAIDevices.length > 0) return childAIDevices;

    // Try to find the AI device in the provided devices list
    const found = devices?.find(d => d.serial_number && d.serial_number.toLowerCase() === mac);
    if (found) return [found];

    // Fallback: show a minimal placeholder so user sees the AI mapping
    return [{
      serial_number: device.ai_device_mac,
      connected: device.connected,
      firmware: device.firmware,
      last_seen: device.last_seen
    }];
  }, [childAIDevices, devices, device]);

  const radioSectionPrivateIfaces = useMemo(
    () => filterRadioInterfacesByMode(device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces),
    [device]
  );
  const radioSectionPublicIfaces = useMemo(
    () => filterRadioInterfacesByMode(device?.public_wifi?.interfaces || device?.public_wifi?.Interfaces),
    [device]
  );

  const filterAccessListDevices = useCallback((devices, query) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return devices;
    return devices.filter((d) => {
      const name = (d.name || '').toLowerCase();
      const serial = (d.serial_number || '').toLowerCase();
      return name.includes(normalized) || serial.includes(normalized);
    });
  }, []);

  const filteredWhitelistDevices = useMemo(
    () => filterAccessListDevices(availableDevices, whitelistDeviceFilter),
    [availableDevices, whitelistDeviceFilter, filterAccessListDevices]
  );

  const filteredBlacklistDevices = useMemo(
    () => filterAccessListDevices(availableDevices, blacklistDeviceFilter),
    [availableDevices, blacklistDeviceFilter, filterAccessListDevices]
  );

  const filteredParentDevices = useMemo(
    () => filterAccessListDevices(availableDevices, parentDeviceFilter),
    [availableDevices, parentDeviceFilter, filterAccessListDevices]
  );

  const renderAccessListSelectFilter = (value, onChange) => (
    <ListSubheader
      sx={{
        lineHeight: 1,
        py: 1,
        px: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder="Type to filter..."
        fullWidth
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </ListSubheader>
  );

  useEffect(() => {
    // Find children of the current device using the devices array
    if (device && devices) {
    // Find AI devices that are children of the current device
      const aiChildren = devices.filter(d => {
        const modelLower = (d?.capabilities?.model || '').toString().toLowerCase();
        const isAI = modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb');
        const isChild = d.parent_serial && 
                       device.serial_number && 
                       d.parent_serial.toLowerCase() === device.serial_number.toLowerCase();
        return isAI && isChild;
      });
      setChildAIDevices(aiChildren);

      // Find jnet2 devices that are children of the current device
      const parentSerial = device.serial_number?.toLowerCase();
      const privIfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces || [];
      const associationStationKeys = collectAssociationStationKeys(privIfaces);

      const jnet2Children = mergeDevicesBySerial(
        devices.filter((d) => {
          if (d.compatible !== 'jnet2' || !parentSerial) return false;
          return d.parent_serial
            && d.parent_serial.toLowerCase() === parentSerial;
        }),
        devices.filter((d) => (
          d.compatible === 'jnet2' && deviceMatchesAssociationStations(d, associationStationKeys)
        )),
      );
      setChildJnet2Devices(jnet2Children);

      // Find EdgeCore children (from associations on this AP)
      // For EdgeCore APs, we get association data directly and attach it to child entries
      const edgeCoreChildren = [];
      const ifaces = privIfaces;

      // Build a map of station -> association data
      const stationAssocMap = new Map();
      ifaces.forEach((iface) => {
        if (!isAPModeIface(iface)) return;
        const assocs = iface.Associations || iface.associations || [];
        assocs.forEach((assoc) => {
          const stationRaw = assoc.Station || assoc.station;
          if (!stationRaw) return;
          const stationKey = normalizeStationKey(stationRaw);
          if (stationKey) {
            stationAssocMap.set(stationKey, {
              rssi: assoc.RSSI || assoc.rssi,
              connectedTime: assoc.ConnectedTime || assoc.connected_time || 0, // connected time in seconds
              rxBytes: assoc.RxBytes || assoc.rx_bytes || 0,
              txBytes: assoc.TxBytes || assoc.tx_bytes || 0,
              rxPackets: assoc.RxPackets || assoc.rx_packets || 0,
              txPackets: assoc.TxPackets || assoc.tx_packets || 0,
              rxRate: assoc.RxRate || assoc.rx_rate,
              txRate: assoc.TxRate || assoc.tx_rate,
              bssid: assoc.BSSID || assoc.bssid,
            });
          }
        });
      });

      if (stationAssocMap.size > 0) {
        // Try to match stations to known devices (serial, wifi_interfaces, radios, station MAC)
        devices.forEach((d) => {
          const matchedKey = deviceAssociationMatchKeys(d).find((k) => stationAssocMap.has(k));
          if (!matchedKey) return;
          const assocData = stationAssocMap.get(matchedKey);
          if (!assocData) return;

          edgeCoreChildren.push({
            ...d,
            // Attach association data for display
            edgeCoreAssoc: assocData,
            // EdgeCore children are always connected if they appear in associations
            edgeCoreOnline: true,
          });
          // Remove from map so we know what's left
          stationAssocMap.delete(matchedKey);
        });
        
        // Stations that can't be matched to a known device are skipped
      }
      setChildEdgeCoreDevices(edgeCoreChildren);

      // If this is an AI device or EdgeCore child device, find its parent network device
      if (device.parent_serial) {
        const modelLower = (device?.capabilities?.model || '').toString().toLowerCase();
        const isAI = modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb');
        const isEdgeCoreChild = device.compatible === 'edgecore_eap102' || 
                               (device.capabilities?.compatible === 'edgecore_eap102');
        
        if (isAI || isEdgeCoreChild) {
          const parent = devices.find(d => 
            d.serial_number && 
            d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
          );
          setParentDevice(parent);
        } else {
          setParentDevice(null);
        }
      } else {
        setParentDevice(null);
      }
    }
  }, [devices, device]);

  // Add new function to get connection details
  const getConnectionDetails = useCallback((deviceToCheck, parentDeviceToCheck) => {
    if (!deviceToCheck || !parentDeviceToCheck) return null;

    const deviceIdentifiers = new Set(deviceAssociationMatchKeys(deviceToCheck));
    if (deviceIdentifiers.size === 0) {
      return null;
    }

    // Search through private and public WiFi interfaces of the parent
    const searchInterfaces = (interfaces, wifiType) => {
      if (!interfaces) {
        return null;
      }
      
      for (const iface of interfaces) {
        // Handle both Associations (capital) and associations (lowercase) for compatibility
        const associations = iface.Associations || iface.associations;
        if (!associations) continue;
        
        for (const assoc of associations) {
          // Handle both Station/station for compatibility
          const station = assoc.Station || assoc.station;
          if (!station) continue;

          const stationKey = normalizeStationKey(station);

          // Get RSSI (handle both RSSI/rssi), rx_rate, tx_rate with case compatibility
          const rssiValue = assoc.RSSI || assoc.rssi;
          const rxRate = assoc.RxRate || assoc.rx_rate;
          const txRate = assoc.TxRate || assoc.tx_rate;

          if (stationKey && deviceIdentifiers.has(stationKey)) {
              return {
              rssi: rssiValue, // This is AP → STA RSSI (TX from AP perspective)
              rxRate: rxRate,
              txRate: txRate
            };
          }
          }
      }
      return null;
    };

    // Get AP → STA RSSI (RX) from the device's station interface
    const getStationRssi = (device) => {
      const privateIfaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces;
      if (!privateIfaces) return null;
      // Handle both Mode/mode for compatibility (jnet2 uses lowercase)
      const stationInterface = privateIfaces.find(iface => 
        (iface.Mode || iface.mode)?.toLowerCase() === 'station'
      );
      // Handle both RSSI/rssi for compatibility
      return stationInterface?.RSSI || stationInterface?.rssi;
    };

    // Search in both private and public WiFi of the parent
    const connectionDetails = searchInterfaces(parentDeviceToCheck.private_wifi?.interfaces || parentDeviceToCheck.private_wifi?.Interfaces, 'private_wifi') ||
                            searchInterfaces(parentDeviceToCheck.public_wifi?.interfaces || parentDeviceToCheck.public_wifi?.Interfaces, 'public_wifi');

    if (connectionDetails) {
      return {
        ...connectionDetails,
        stationRssi: getStationRssi(deviceToCheck) // Add AP → STA RSSI
      };
    }

    return null;
  }, []);

  // Prefer the child's STA-mode `uplink` profile (RSSI + radio TX/RX
  // rate histograms) over the parent's AP-side Association entry for
  // MCS/NSS/BW/rate. The AP-side association sums per-station rate
  // samples across every client on the same parent radio, so MCS,
  // NSS and BW are aliased and effectively unusable for per-edge
  // attribution. The child's STA radio histograms describe THIS
  // single edge (the child has exactly one STA interface), so they
  // are the authoritative source the optimizer also consumes.
  //
  // Falls back to the legacy parent-side association data for any
  // device that does not yet have an `uplink` block populated
  // (legacy firmware that didn't report enough counters, EdgeCore
  // children, or jnet2 devices whose STA interface has no SSID set
  // yet — e.g. immediately after boot before the mesh is joined).
  const getUplinkDetails = useCallback((childDevice, parentDevice) => {
    const fallback = getConnectionDetails(childDevice, parentDevice);
    const u = childDevice?.uplink;
    if (!u || (!u.tx_profile && !u.rx_profile && !u.rssi)) {
      return fallback;
    }

    // Convert a RadioRateProfile (as returned by the backend
    // `enrichUplinkForResponse`) into the legacy `{Bitrate, NSS,
    // MCS, BW}` shape the existing render code already understands.
    // `Bitrate` here is an estimated PHY rate derived from the
    // dominant MCS/NSS/BW tuple, not measured application throughput.
    const toRate = (profile) => {
      if (!profile) return null;
      const out = {};
      const mcs = profile.data_dominant_mcs || profile.dominant_mcs;
      if (mcs) out.MCS = mcs;
      if (profile.dominant_nss) out.NSS = profile.dominant_nss;
      if (profile.dominant_bw_mhz) out.BW = profile.dominant_bw_mhz;
      // est_kbps comes from algo.UplinkProfileKbps server-side.
      // Convert kbps → bps to match the legacy `Bitrate` units the
      // render code divides by 1_000_000 for Mbps display.
      if (profile.est_kbps) out.Bitrate = profile.est_kbps * 1000;
      if (profile.est_kbps) out.Estimated = true;
      return Object.keys(out).length ? out : null;
    };

    const tx = toRate(u.tx_profile);
    const rx = toRate(u.rx_profile);

    // The STA-mode interface reports a single RSSI (child's view of
    // the parent AP). We surface it as both `rssi` (the original
    // "TX direction" RSSI label) and `stationRssi` (the original
    // "RX direction" label) because the new pipeline doesn't
    // distinguish a separate AP→STA vs STA→AP RSSI per direction —
    // it's the same SNR measurement either way.
    const childRssi = u.rssi || null;
    return {
      rssi: childRssi || fallback?.rssi || null,
      stationRssi: childRssi || fallback?.stationRssi || null,
      rxRate: rx || fallback?.rxRate || null,
      txRate: tx || fallback?.txRate || null,
      source: (tx || rx || childRssi) ? 'uplink' : 'association',
      txTotalBytes: u.tx_profile?.total_bytes,
      rxTotalBytes: u.rx_profile?.total_bytes,
      parentSSID: u.parent_ssid,
    };
  }, [getConnectionDetails]);

  const childLinkCardTitle = useCallback((perspective, source) => {
    const base = perspective === 'ap' ? 'AP hears child' : 'Child hears AP';
    return source === 'association' ? `${base} · AP assoc` : base;
  }, []);

  // New function to fetch configuration
  const fetchDeviceConfig = async () => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      console.log('Fetching device configuration for serial:', serial);
      const configData = await api.getDeviceConfig(serial);
      console.log('Config data received:', configData);
      const formattedConfig = JSON.stringify(configData.configuration || {}, null, 2);
      setConfigJson(formattedConfig);
      setOriginalConfig(formattedConfig);
    } catch (err) {
      console.error('Error fetching device configuration:', err);
      setConfigError(err.response?.data?.message || err.message || 'Failed to fetch device configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const fetchTreeMgmtView = useCallback(async () => {
    try {
      setTreeMgmtLoading(true);
      setTreeMgmtError(null);
      const view = await api.getTreeMgmtConfig(serial);
      setTreeMgmtView(view);
      setTreeMgmtEdits({});
    } catch (err) {
      console.error('Error fetching tree-mgmt view:', err);
      setTreeMgmtError(err.message || 'Failed to fetch tree-management view');
    } finally {
      setTreeMgmtLoading(false);
    }
  }, [serial]);

  const setTreeEdit = (treeId, field, value) => {
    setTreeMgmtEdits((prev) => {
      const next = { ...prev };
      const entry = { ...(next[treeId] || {}) };
      if (value === undefined || value === null) {
        delete entry[field];
      } else {
        // Keep empty string so numeric fields can be cleared while typing.
        entry[field] = value;
      }
      if (Object.keys(entry).length === 0) {
        delete next[treeId];
      } else {
        next[treeId] = entry;
      }
      return next;
    });
  };

  const parseEditedInt = (raw, treeId, label) => {
    if (raw === '') {
      return { error: `${treeId}: ${label} cannot be empty` };
    }
    if (label === 'self_improvment' && isBgSelfImprovementAlways(raw)) {
      return { value: BG_SELF_IMPROVEMENT_ALWAYS };
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) {
      return { error: `${treeId}: ${label} must be an integer` };
    }
    return { value: n };
  };

  // Returns 'single' | 'group_ids' | 'groups' for the tree, preferring the
  // user's pending edits and falling back to the server view.
  const channelModeForTree = (tree) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (edits.channel_mode) return edits.channel_mode;
    if (Array.isArray(tree.channel_group_ids) && tree.channel_group_ids.length > 0) {
      return 'group_ids';
    }
    if (tree.channel_groups && Object.keys(tree.channel_groups).length > 0) {
      return 'groups';
    }
    return 'single';
  };

  // Best-effort parser for the channel_group_ids edit value (which may be a
  // CSV string, an array, or even an array of {id} options from Autocomplete).
  // Filters down to the valid known UNII ids; never returns undefined so the
  // UI can always render the multi-select state.
  const parseChannelGroupIds = (raw) => {
    if (raw === '' || raw === undefined || raw === null) return [];
    let arr;
    if (Array.isArray(raw)) {
      arr = raw.map((v) => (typeof v === 'object' && v !== null ? v.id : v));
    } else {
      arr = String(raw).split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    }
    const out = [];
    for (const v of arr) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && UNII_BY_ID[n]) out.push(n);
    }
    return out;
  };

  // Parses a CSV/space separated list of integers; returns undefined on error
  // (caller decides whether to surface validation).
  const parseIntList = (raw) => {
    if (raw === '' || raw === undefined || raw === null) return [];
    const parts = String(raw).split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    const out = [];
    for (const p of parts) {
      const n = parseInt(p, 10);
      if (Number.isNaN(n)) return undefined;
      out.push(n);
    }
    return out;
  };

  const buildTreeMgmtPatch = () => {
    if (!treeMgmtView) return { trees: [], errors: [] };
    const trees = [];
    const errors = [];
    for (const tree of treeMgmtView.trees || []) {
      const edits = treeMgmtEdits[tree.tree_id];
      if (!edits || Object.keys(edits).length === 0) continue;
      const entry = { tree_id: tree.tree_id };
      let touched = false;
      if (edits.band !== undefined) { entry.band = edits.band; touched = true; }
      if (edits.htmode !== undefined) { entry.htmode = edits.htmode; touched = true; }
      if (edits.tx_power_up !== undefined) {
        const parsed = parseEditedInt(edits.tx_power_up, tree.tree_id, 'tx_power_up');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.tx_power_up = parsed.value;
        touched = true;
      }
      if (edits.tx_power_down !== undefined) {
        const parsed = parseEditedInt(edits.tx_power_down, tree.tree_id, 'tx_power_down');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.tx_power_down = parsed.value;
        touched = true;
      }
      if (edits.max_children !== undefined) {
        const parsed = parseEditedInt(edits.max_children, tree.tree_id, 'max_children');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.max_children = parsed.value;
        touched = true;
      }

      // Channel selection — only emit the field for the currently-active mode.
      // When in a band-bound mode (group_ids / groups) we also drop any
      // selectors from the other band so we never round-trip a cross-band
      // selection through the API.
      const mode = channelModeForTree(tree);
      const effectiveBand = (edits.band !== undefined ? edits.band : tree.band) || '';
      if (mode === 'single' && edits.channel !== undefined) {
        const parsed = parseEditedInt(edits.channel, tree.tree_id, 'channel');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.channel = parsed.value;
        touched = true;
      } else if (mode === 'group_ids' && edits.channel_group_ids !== undefined) {
        const ids = parseIntList(edits.channel_group_ids);
        if (ids === undefined) {
          errors.push(`${tree.tree_id}: channel_group_ids must be a list of integers`);
          continue;
        }
        const filtered = effectiveBand
          ? ids.filter(id => UNII_BY_ID[id]?.band === effectiveBand)
          : ids;
        entry.channel_group_ids = filtered;
        touched = true;
      } else if (mode === 'groups') {
        const groups = {};
        let groupTouched = false;
        for (const band of ['5GHz', '6GHz']) {
          if (effectiveBand && band !== effectiveBand) continue;
          const key = `channel_groups_${band}`;
          if (edits[key] !== undefined) {
            const list = parseIntList(edits[key]);
            if (list === undefined) {
              errors.push(`${tree.tree_id}: channel_groups[${band}] must be a list of integers`);
              continue;
            }
            groups[band] = list;
            groupTouched = true;
          }
        }
        if (groupTouched) {
          entry.channel_groups = groups;
          touched = true;
        }
      }

      // Background STA scan — pull out bg_* edits into a sub-object.
      const bg = {};
      let bgTouched = false;
      let bgError = false;
      const bgFields = [
        ['bg_bg_scan_on_off', 'bg_scan_on_off', 'string'],
        ['bg_chan_scan_interval', 'chan_scan_interval', 'int'],
        ['bg_fr_on_off', 'fr_on_off', 'string'],
        ['bg_scan_interval', 'scan_interval', 'int'],
        ['bg_self_improvment', 'self_improvment', 'int'],
      ];
      for (const [editKey, apiKey, kind] of bgFields) {
        if (edits[editKey] === undefined) continue;
        if (kind === 'int') {
          const parsed = parseEditedInt(edits[editKey], tree.tree_id, apiKey);
          if (parsed.error) {
            errors.push(parsed.error);
            bgError = true;
            break;
          }
          bg[apiKey] = parsed.value;
        } else {
          bg[apiKey] = edits[editKey];
        }
        bgTouched = true;
      }
      if (bgError) continue;
      if (bgTouched) {
        entry.background_sta_scan = bg;
        touched = true;
      }

      if (touched) trees.push(entry);
    }
    return { trees, errors };
  };

  const handleSaveTreeMgmt = async () => {
    const built = buildTreeMgmtPatch();
    if (built.errors && built.errors.length > 0) {
      setSaveStatus({ type: 'error', message: built.errors.join('; ') });
      return;
    }
    if (built.trees.length === 0) {
      setSaveStatus({ type: 'warning', message: 'No field changes to apply' });
      return;
    }
    try {
      const resp = await api.patchTreeMgmtConfig(serial, { trees: built.trees });
      setSaveStatus({ type: 'success', message: 'Tree-management settings applied' });
      if (resp && resp.view) {
        setTreeMgmtView(resp.view);
        setTreeMgmtEdits({});
      }
      setTimeout(() => {
        setConfigDialogOpen(false);
        setSaveStatus({ type: '', message: '' });
      }, 1500);
    } catch (err) {
      setSaveStatus({ type: 'error', message: err.message || 'Failed to apply tree-management settings' });
    }
  };

  // Returns the value to show in the input for a given (tree_id, field): the
  // pending edit if present, otherwise the value from the server view.
  const treeFieldValue = (tree, field) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (Object.prototype.hasOwnProperty.call(edits, field)) {
      return edits[field];
    }
    const v = tree[field];
    if (v === undefined || v === null) return '';
    return typeof v === 'number' ? String(v) : v;
  };

  // Returns the displayed value for one channel-group-ids / channel-groups
  // input box (always a string suitable for a TextField). Pending edits win
  // over the server view.
  const channelGroupIdsValue = (tree) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (Object.prototype.hasOwnProperty.call(edits, 'channel_group_ids')) {
      return edits.channel_group_ids;
    }
    const ids = tree.channel_group_ids;
    if (!Array.isArray(ids)) return '';
    return ids.join(', ');
  };

  const channelGroupsValueForBand = (tree, band) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    const key = `channel_groups_${band}`;
    if (Object.prototype.hasOwnProperty.call(edits, key)) {
      return edits[key];
    }
    const groups = tree.channel_groups || {};
    const list = groups[band];
    if (!Array.isArray(list)) return '';
    return list.join(', ');
  };

  // Returns the displayed value for one background_sta_scan sub-field.
  const bgFieldValue = (tree, apiKey) => {
    const editKey = `bg_${apiKey}`;
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (Object.prototype.hasOwnProperty.call(edits, editKey)) {
      return edits[editKey];
    }
    const bg = tree.background_sta_scan || {};
    const v = bg[apiKey];
    if (v === undefined || v === null) return '';
    return typeof v === 'number' ? String(v) : v;
  };

  const bgSelfImprovementDisplay = (tree) => formatBgSelfImprovementDisplay(bgFieldValue(tree, 'self_improvment'));

  const bgChannelGroupsDisplayForBand = (tree, band) => {
    const bg = tree.background_sta_scan || {};
    const groups = bg.channel_groups || {};
    const list = groups[band];
    if (!Array.isArray(list) || list.length === 0) return '—';
    return list.join(', ');
  };

  // ---- Add Tree dialog ----
  // `channel_mode` mirrors the inline editor: 'single' (channel), 'group_ids'
  // (UNII multi-select), or 'groups' (explicit per-band channel lists). The
  // submit handler emits exactly one of (channel | channel_group_ids |
  // channel_groups) based on this mode so we don't trip the backend's
  // mutual-exclusion validation.
  const [addTreeOpen, setAddTreeOpen] = useState(false);
  const [addTreeDraft, setAddTreeDraft] = useState({
    tree_id: '',
    band: '5GHz',
    channel_mode: 'single',
    channel: '',
    channel_group_ids: [],
    channel_groups_5GHz: '',
    channel_groups_6GHz: '',
    htmode: '',
    tx_power_up: '',
    tx_power_down: '',
    max_children: '',
  });
  const [addTreeStatus, setAddTreeStatus] = useState({ type: '', message: '' });

  // Backend cap: Node = 1 treemgmt entry, CAP = 2.
  const maxAllowedTrees = () => {
    if (!treeMgmtView) return 0;
    return treeMgmtView.device_type === 'cap' ? 2 : 1;
  };

  // Returns the radio names defined on the device that don't yet have a
  // matching treemgmt entry. On Node devices the 2.4GHz radio is excluded
  // (it cannot host treemgmt entries). Returns [] if we can't parse the
  // raw config — the backend will still validate.
  const availableTreeIDs = () => {
    if (!treeMgmtView) return [];
    const isCap = treeMgmtView.device_type === 'cap';
    let radios = [];
    try {
      const cfg = JSON.parse(configJson || '{}');
      if (Array.isArray(cfg.radio)) {
        radios = cfg.radio
          .filter(r => r && typeof r === 'object' && r.radio)
          // band code "1" == 2.4GHz; Nodes can't run mesh on 2.4GHz.
          .filter(r => isCap || String(r.band) !== '1')
          .map(r => r.radio);
      }
    } catch (_) {
      // Ignore — we'll fall through to "no choices available".
    }
    const used = new Set((treeMgmtView.trees || []).map(t => (t.tree_id || '').toLowerCase()));
    return radios.filter(r => !used.has(String(r).toLowerCase()));
  };

  const canAddTree = () => {
    if (!treeMgmtView) return false;
    const used = (treeMgmtView.trees || []).length;
    return used < maxAllowedTrees();
  };

  const openAddTreeDialog = () => {
    const candidates = availableTreeIDs();
    setAddTreeDraft({
      tree_id: candidates[0] || '',
      band: '5GHz',
      channel_mode: 'single',
      channel: '',
      channel_group_ids: [],
      channel_groups_5GHz: '',
      channel_groups_6GHz: '',
      htmode: '',
      tx_power_up: '',
      tx_power_down: '',
      max_children: '',
    });
    setAddTreeStatus({ type: '', message: '' });
    setAddTreeOpen(true);
  };

  const handleAddTreeSubmit = async () => {
    if (!addTreeDraft.tree_id) {
      setAddTreeStatus({ type: 'error', message: 'tree_id is required' });
      return;
    }
    if (!addTreeDraft.band) {
      setAddTreeStatus({ type: 'error', message: 'band is required' });
      return;
    }
    const body = { tree_id: addTreeDraft.tree_id, band: addTreeDraft.band };
    // Emit the channel selection that matches the chosen mode. The backend
    // rejects requests that set more than one of these fields, so we make
    // sure exactly one shows up.
    if (addTreeDraft.channel_mode === 'single' && addTreeDraft.channel !== '') {
      body.channel = parseInt(addTreeDraft.channel, 10);
    } else if (addTreeDraft.channel_mode === 'group_ids' && addTreeDraft.channel_group_ids.length > 0) {
      // Defensive: only emit IDs that match the chosen band. The UI already
      // filters them on band change but we re-check here in case the draft
      // got out of sync (e.g. directly mutated).
      body.channel_group_ids = addTreeDraft.channel_group_ids
        .filter(id => UNII_BY_ID[id]?.band === addTreeDraft.band);
    } else if (addTreeDraft.channel_mode === 'groups') {
      const groups = {};
      // Only the field for the selected band is enabled in the UI; ignore
      // the other one even if it has a residual value.
      if (addTreeDraft.band === '5GHz') {
        const five = parseIntList(addTreeDraft.channel_groups_5GHz);
        if (five === undefined) {
          setAddTreeStatus({ type: 'error', message: 'channel_groups[5GHz] must be a comma-separated list of integers' });
          return;
        }
        if (five.length) groups['5GHz'] = five;
      } else if (addTreeDraft.band === '6GHz') {
        const six = parseIntList(addTreeDraft.channel_groups_6GHz);
        if (six === undefined) {
          setAddTreeStatus({ type: 'error', message: 'channel_groups[6GHz] must be a comma-separated list of integers' });
          return;
        }
        if (six.length) groups['6GHz'] = six;
      }
      if (Object.keys(groups).length > 0) body.channel_groups = groups;
    }
    if (addTreeDraft.htmode) body.htmode = addTreeDraft.htmode;
    if (addTreeDraft.tx_power_up !== '') body.tx_power_up = parseInt(addTreeDraft.tx_power_up, 10);
    if (addTreeDraft.tx_power_down !== '') body.tx_power_down = parseInt(addTreeDraft.tx_power_down, 10);
    if (addTreeDraft.max_children !== '') body.max_children = parseInt(addTreeDraft.max_children, 10);
    try {
      const resp = await api.addTreeMgmtEntry(serial, body);
      if (resp && resp.view) setTreeMgmtView(resp.view);
      setAddTreeOpen(false);
      setSaveStatus({ type: 'success', message: `Added tree ${body.tree_id}` });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 2000);
    } catch (err) {
      setAddTreeStatus({ type: 'error', message: err.message || 'Failed to add tree' });
    }
  };

  const handleOpenParentDialog = () => {
    console.log('Opening parent dialog with devices:', devices);
    console.log('Current device serial:', serial);
    
    // Filter out only the current device
    const filteredDevices = devices.filter(d => {
      if (!d || !d.serial_number) {
        console.log('Skipping device with missing serial_number:', d);
        return false;
      }
      const isNotCurrentDevice = d.serial_number.toLowerCase() !== serial.toLowerCase();
      console.log(`Device ${d.serial_number}: isNotCurrentDevice=${isNotCurrentDevice}, connected=${d.connected}`);
      return isNotCurrentDevice;
    });
    
    console.log('Filtered devices for parent selection:', filteredDevices);
    setAvailableDevices(filteredDevices);
    setSelectedParent(device?.parent_serial || '');
    setParentDialogOpen(true);
  };

  const handleCloseParentDialog = () => {
    setParentDialogOpen(false);
    setSelectedParent('');
    setParentDeviceFilter('');
    setParentUpdateStatus({ type: '', message: '' });
  };

  const handleSetParent = async () => {
    try {
      await api.setParent(serial, selectedParent);
      setParentUpdateStatus({ type: 'success', message: 'Parent updated successfully' });
      // Update local device state instead of refreshing
      setDevice(prevDevice => ({
        ...prevDevice,
        parent_serial: selectedParent
      }));
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseParentDialog();
      }, 1500);
    } catch (err) {
      setParentUpdateStatus({ 
        type: 'error', 
        message: err.response?.data?.message || err.message || 'Failed to update parent' 
      });
    }
  };

  const handleSaveConfig = async () => {
    try {
      let parsedConfig;
      try {
        parsedConfig = JSON.parse(configJson);
      } catch (e) {
        setSaveStatus({ type: 'error', message: 'Invalid JSON format' });
        return;
      }

      const response = await api.configureDevice(serial, parsedConfig);
      
      // Check for the specific error message
      if (response.error === "Failed to send command to device") {
        setSaveStatus({ type: 'warning', message: 'Configuration saved but not yet applied' });
      } else {
        setSaveStatus({ type: 'success', message: 'Configuration saved successfully' });
      }
      setOriginalConfig(configJson);

      // Close dialog after showing success message for 1.5 seconds
      setTimeout(() => {
        setConfigDialogOpen(false);
        setSaveStatus({ type: '', message: '' });
      }, 1500);
    } catch (err) {
      setSaveStatus({ type: 'error', message: err.response?.data?.message || err.message || 'Failed to save configuration' });
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(configJson);
      const formatted = JSON.stringify(parsed, null, 2);
      setConfigJson(formatted);
      setIsJsonValid(true);
    } catch (e) {
      setIsJsonValid(false);
      setSaveStatus({ type: 'error', message: 'Invalid JSON format' });
    }
  };

  const handleReset = () => {
    setConfigJson(originalConfig);
    setIsJsonValid(true);
    setSaveStatus({ type: '', message: '' });
  };

  const handleEditorChange = (value) => {
    setConfigJson(value);
    try {
      JSON.parse(value);
      setIsJsonValid(true);
      setSaveStatus({ type: '', message: '' });
    } catch (e) {
      setIsJsonValid(false);
    }
  };

  const handleStartRtty = () => {
    // Simply open the RTTY page in a new tab
    window.open(`/rtty/${serial}`, '_blank');
  };



  const handleOpenAccessListDialog = async () => {
    const filteredDevices = devices.filter(d => {
      if (!d || !d.serial_number) {
        return false;
      }
      const isNotCurrentDevice = d.serial_number.toLowerCase() !== serial.toLowerCase();
      const isJnet2Compatible = d.compatible === 'jnet2';
      return isNotCurrentDevice && isJnet2Compatible;
    });
    setAvailableDevices(filteredDevices);

    try {
      const accessListData = await api.getAccessList(serial);
      const currentWhitelist = accessListData.whitelist ? accessListData.whitelist.split(',').filter(Boolean) : [];
      const currentBlacklist = accessListData.blacklist ? accessListData.blacklist.split(',').filter(Boolean) : [];
      setSelectedWhitelist(currentWhitelist);
      setSelectedBlacklist(currentBlacklist);
      const insist = accessListData.insist_time_sec ?? device?.tm_config?.whitelist_insist_time_sec;
      setAccessListInsistSec(insist !== undefined && insist !== null ? String(insist) : '');
    } catch (error) {
      console.error('Error fetching access list:', error);
      const tmConfig = device?.tm_config || {};
      const currentWhitelist = tmConfig.white_list ? tmConfig.white_list.split(',').filter(Boolean) : [];
      const currentBlacklist = tmConfig.black_list ? tmConfig.black_list.split(',').filter(Boolean) : [];
      setSelectedWhitelist(currentWhitelist);
      setSelectedBlacklist(currentBlacklist);
      const insist = tmConfig.whitelist_insist_time_sec;
      setAccessListInsistSec(insist !== undefined && insist !== null ? String(insist) : '');
    }
    setAccessListDialogOpen(true);
  };

  const handleCloseAccessListDialog = () => {
    setAccessListDialogOpen(false);
    setSelectedWhitelist([]);
    setSelectedBlacklist([]);
    setAccessListInsistSec('');
    setWhitelistDeviceFilter('');
    setBlacklistDeviceFilter('');
    setAccessListUpdateStatus({ type: '', message: '' });
  };

  const handleSetAccessList = async () => {
    const trimmedInsist = String(accessListInsistSec ?? '').trim();
    if (trimmedInsist !== '') {
      const insistNum = parseInt(trimmedInsist, 10);
      if (Number.isNaN(insistNum) || insistNum < 1 || insistNum > 600) {
        setAccessListUpdateStatus({
          type: 'error',
          message: 'Whitelist insist time must be between 1 and 600 seconds (or leave empty to keep current).',
        });
        return;
      }
    }

    try {
      const insistTimeSec = trimmedInsist !== '' ? parseInt(trimmedInsist, 10) : undefined;
      const accessResp = await api.setAccessList(serial, selectedWhitelist, selectedBlacklist, insistTimeSec);
      const offlineNote = Boolean(accessResp?.device_offline);
      setAccessListUpdateStatus({
        type: 'success',
        message: offlineNote
          ? (accessResp?.message || 'Configuration saved. Device is offline and will apply when connected.')
          : 'Access lists updated successfully',
      });
      
      // Update local device state with new tm_config
      setDevice(prevDevice => {
        const nextTm = {
          ...prevDevice.tm_config,
          white_list: selectedWhitelist.join(','),
          black_list: selectedBlacklist.join(','),
        };
        if (trimmedInsist !== '') {
          nextTm.whitelist_insist_time_sec = parseInt(trimmedInsist, 10);
        }
        return {
          ...prevDevice,
          tm_config: nextTm,
        };
      });
      
      // Refresh device data to get updated state
      setTimeout(() => {
        fetchDeviceData();
      }, 1000);
      
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseAccessListDialog();
      }, 1500);
    } catch (err) {
      console.error('Error updating access lists:', err);
      setAccessListUpdateStatus({
        type: 'error',
        message: err.response?.data?.message || err.message || 'Failed to update access lists'
      });
    }
  };

  const handleWhitelistChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedWhitelist(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleBlacklistChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedBlacklist(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleOpenCommandDialog = () => {
    setCommandDialogOpen(true);
    setCommand('');
    setCommandStatus({ type: '', message: '' });
  };

  const handleCloseCommandDialog = () => {
    setCommandDialogOpen(false);
    setCommand('');
    setCommandStatus({ type: '', message: '' });
  };

  const handleSendCommand = async () => {
    try {
      await api.sendCommand(serial, command);
      setCommandStatus({ type: 'success', message: 'Command sent successfully' });
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseCommandDialog();
      }, 1500);
    } catch (err) {
      setCommandStatus({ 
        type: 'error', 
        message: err.response?.data?.message || err.message || 'Failed to send command' 
      });
    }
  };

  const getDeviceIcon = () => {
    if (!device) return null;
    
    const model = device.capabilities?.model?.toLowerCase() || '';
    const compatible = device.compatible || device.capabilities?.compatible || '';
    const isAI = model.includes('nvidia') || model.includes('qrb5165-128gb');
    const isCAP = model.includes('cap');
    const isNode = model.includes('node');
    const isEdgeCore = compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102');

    if (isAI) {
      return <VideocamIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />;
    } else if (isEdgeCore) {
      return <RouterIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />;
    } else if (isCAP) {
      return <RouterIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />;
    } else if (isNode) {
      return <LightIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />;
    }
    return null;
  };

  const handleOpenConfigDialog = async () => {
    setConfigMode('fields');
    setSaveStatus({ type: '', message: '' });
    setConfigDialogOpen(true);
    // Fetch both views up-front so toggling between Fields and Raw is instant.
    await Promise.all([fetchTreeMgmtView(), fetchDeviceConfig()]);
  };

  const handleSwitchConfigMode = (mode) => {
    setConfigMode(mode);
    setSaveStatus({ type: '', message: '' });
  };

  const handleExportState = async () => {
    try {
      setIsLoading(true);
      console.log('Exporting device state for serial:', serial);
      const reportData = await api.getDeviceReport(serial);
      
      // Create a blob with the JSON data
      const jsonString = JSON.stringify(reportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `device_report_${serial}_${new Date().toISOString().split('T')[0]}.json`;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Device state exported successfully');
    } catch (error) {
      console.error('Error exporting device state:', error);
      // You could add a snackbar or alert here to show the error to the user
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Device Details
          </Typography>
          {(userInfo || environment) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
              {userInfo && userInfo.name && (
                <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {userInfo.name}
                </Typography>
              )}
              {environment && (
                <Typography
                  variant="body2"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    textTransform: 'capitalize',
                    opacity: 0.9
                  }}
                >
                  ({environment})
                </Typography>
              )}
            </Box>
          )}
          <IconButton
            color="inherit"
            onClick={handleLogoutClick}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <LogoutIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, width: '100%' }}>
        <Box sx={{ maxWidth: 1200, margin: '20px auto', padding: 2 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back to Device List
          </Button>

          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={serial}
              sx={{ 
                height: 32,
                fontSize: '1rem',
                fontWeight: 500,
                bgcolor: device?.connected ? 'success.main' : 'error.main',
                color: 'white',
                border: '1px solid',
                borderColor: device?.connected ? 'success.dark' : 'error.dark',
                '& .MuiChip-label': {
                  px: 2
                }
              }}
            />
            {device?.capabilities?.model && (
              <Chip
                label={(() => {
                  const model = device.capabilities.model.toLowerCase();
                  const compatible = device.compatible || device.capabilities?.compatible || '';
                  if (compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102')) {
                    return 'EdgeCore';
                  } else if (model.includes('nvidia') || model.includes('qrb5165-128gb')) {
                    return 'AI';
                  } else if (model.includes('cap')) {
                    return 'CAP';
                  } else {
                    return 'Node';
                  }
                })()}
                sx={{ 
                  height: 32,
                  fontSize: '1rem',
                  fontWeight: 500,
                  bgcolor: (() => {
                    const model = device.capabilities.model.toLowerCase();
                    const compatible = device.compatible || device.capabilities?.compatible || '';
                    if (compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102')) {
                      return 'warning.lighter';
                    } else if (model.includes('nvidia') || model.includes('qrb5165-128gb')) {
                      return 'primary.lighter';
                    } else if (model.includes('cap')) {
                      return 'info.lighter';
                    } else {
                      return 'warning.lighter';
                    }
                  })(),
                  color: (() => {
                    const model = device.capabilities.model.toLowerCase();
                    const compatible = device.compatible || device.capabilities?.compatible || '';
                    if (compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102')) {
                      return 'warning.main';
                    } else if (model.includes('nvidia') || model.includes('qrb5165-128gb')) {
                      return 'primary.main';
                    } else if (model.includes('cap')) {
                      return 'info.main';
                    } else {
                      return 'warning.main';
                    }
                  })(),
                  border: '1px solid',
                  borderColor: (() => {
                    const model = device.capabilities.model.toLowerCase();
                    const compatible = device.compatible || device.capabilities?.compatible || '';
                    if (compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102')) {
                      return 'warning.main';
                    } else if (model.includes('nvidia') || model.includes('qrb5165-128gb')) {
                      return 'primary.main';
                    } else if (model.includes('cap')) {
                      return 'info.main';
                    } else {
                      return 'warning.main';
                    }
                  })(),
                  '& .MuiChip-label': {
                    px: 2
                  }
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {canWriteBackhaul && device?.compatible === 'jnet2' && (
              <>
                <Tooltip title="Manage Access Lists">
                  <IconButton
                    onClick={handleOpenAccessListDialog}
                    size="small"
                    sx={{ 
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <AccessListIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Set Parent">
                  <IconButton
                    onClick={handleOpenParentDialog}
                    size="small"
                    sx={{ 
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <LinkIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export State">
                  <IconButton
                    onClick={handleExportState}
                    size="small"
                    disabled={isLoading}
                    sx={{ 
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      },
                      '&.Mui-disabled': {
                        borderColor: 'divider',
                        backgroundColor: 'action.disabledBackground'
                      }
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <DeviceReportMenuButton serial={device?.serial_number || serial} />
              </>
            )}
            {canWriteBackhaul && (
            <>
            <Tooltip title="Command">
              <IconButton
                onClick={handleOpenCommandDialog}
                size="small"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <CommandIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="RTTY">
              <IconButton
                onClick={handleStartRtty}
                size="small"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <RttyIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure">
              <IconButton
                onClick={handleOpenConfigDialog}
                size="small"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ConfigureIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            {isLastKnownGoodEligible(device) && (
            <LastKnownGoodMenu
              scope="device"
              serial={serial}
              buttonSx={{
                width: '28px',
                height: '28px',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
              onComplete={({ type, message }) => {
                setLkgStatus({ type, message });
                if (type === 'success') {
                  fetchDeviceData();
                  fetchDeviceConfig();
                  setTimeout(() => setLkgStatus({ type: '', message: '' }), 5000);
                }
              }}
            />
            )}
            </>
            )}
          </Box>
        </Box>

        {lkgStatus.message && (
          <Alert severity={lkgStatus.type || 'info'} sx={{ mb: 2 }}>
            {lkgStatus.message}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {getDeviceIcon()}
                <Typography variant="h6">Device Information</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Serial Number:</Typography>
                    <Chip
                      size="small"
                      label={device.serial_number}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>IP Address:</Typography>
                    <Chip
                      size="small"
                      label={getPeerAddress(device.peer_address) || 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {device.cap_serial && device.cap_serial.toLowerCase() !== device.serial_number.toLowerCase() && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>CAP Serial:</Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/device/${device.cap_serial.toLowerCase()}`)}
                        sx={{ 
                          textTransform: 'none', 
                          p: 0, 
                          minWidth: 0,
                          justifyContent: 'flex-start'
                        }}
                      >
                        <Chip
                          size="small"
                          label={device.cap_serial}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Uptime:</Typography>
                    <Chip
                      size="small"
                      label={device.uptime ? (() => {
                        const days = Math.floor(device.uptime / 86400);
                        const hours = Math.floor((device.uptime % 86400) / 3600);
                        const minutes = Math.floor((device.uptime % 3600) / 60);
                        
                        if (days > 0) {
                          return days >= 7 ? `${Math.floor(days / 7)}w ${days % 7}d` : `${days}d ${hours}h`;
                        } else if (hours > 0) {
                          return `${hours}h ${minutes}m`;
                        } else {
                          return `${minutes}m`;
                        }
                      })() : 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Last Seen:</Typography>
                    <Chip
                      size="small"
                      label={device.last_seen ? new Date(device.last_seen).toLocaleString() : 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Connected Time:</Typography>
                    <Chip
                      size="small"
                      label={(() => {
                        // Get connected time from parent's WiFi associations
                        const getConnectedTime = () => {
                          if (!device || !devices || !device.parent_serial) return null;
                          
                          // Find parent device
                          const parent = devices.find(d => 
                            d.serial_number && 
                            d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
                          );
                          
                          if (!parent || (!parent.private_wifi?.interfaces && !parent.private_wifi?.Interfaces)) return null;
                          
                          // Build device identifiers: MACs from radios AND serial number
                          // jnet2 uses serial number in associations, EdgeCore uses MAC
                          const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                          const deviceSerial = device.serial_number?.toLowerCase();
                          const deviceIdentifiers = new Set(deviceMacs);
                          if (deviceSerial) {
                            deviceIdentifiers.add(deviceSerial);
                            deviceIdentifiers.add(deviceSerial.replace(/:/g, ''));
                          }
                          if (deviceIdentifiers.size === 0) return null;
                          
                          // Look for this device in parent's associations
                          const parentIfaces = parent.private_wifi?.interfaces || parent.private_wifi?.Interfaces;
                          for (const iface of parentIfaces || []) {
                            // Handle both Associations/associations for compatibility
                            const associations = iface.Associations || iface.associations;
                            if (associations) {
                              for (const assoc of associations) {
                                // Handle both Station/station and ConnectedTime/connected_time
                                const station = assoc.Station || assoc.station;
                                const connectedTime = assoc.ConnectedTime || assoc.connected_time;
                                const stationId = station?.toLowerCase().replace(/:/g, '');
                                if ((deviceIdentifiers.has(stationId) || deviceIdentifiers.has(station?.toLowerCase())) && connectedTime) {
                                  return connectedTime;
                                }
                              }
                            }
                          }
                          return null;
                        };
                        
                        const connectedTime = getConnectedTime();
                        if (!connectedTime) return 'N/A';
                        
                        // Format connected time similar to uptime
                        const days = Math.floor(connectedTime / 86400);
                        const hours = Math.floor((connectedTime % 86400) / 3600);
                        const minutes = Math.floor((connectedTime % 3600) / 60);
                        
                        if (days > 0) {
                          return days >= 7 ? `${Math.floor(days / 7)}w ${days % 7}d` : `${days}d ${hours}h`;
                        } else if (hours > 0) {
                          return `${hours}h ${minutes}m`;
                        } else {
                          return `${minutes}m`;
                        }
                      })()}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {device.parent_serial && device.parent_serial !== 'N/A' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Parent Serial:</Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/device/${device.parent_serial.toLowerCase()}`)}
                        sx={{ 
                          textTransform: 'none', 
                          p: 0, 
                          minWidth: 0,
                          justifyContent: 'flex-start'
                        }}
                      >
                        <Chip
                          size="small"
                          label={device.parent_serial}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Firmware:</Typography>
                    <Chip
                      size="small"
                      label={device.firmware || 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Compatible:</Typography>
                    <Chip
                      size="small"
                      label={device.compatible || 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {(device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null) ||
                   (device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null) ||
                   (getParentLatencyMs(device) !== null) ||
                   (getCapLatencyMs(device) !== null) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Backhaul Health:</Typography>
                      
                      {/* TX Ratio */}
                      {device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary', fontWeight: 'bold' }}>TX Ratio:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Transmission success ratio - percentage of packets successfully transmitted over the backhaul link. Higher values indicate better link quality.">
                              <Chip
                                size="small"
                                label={`${(device.backhaul_tx_ratio * 100).toFixed(1)}%`}
                                color={
                                  device.backhaul_tx_ratio >= 0.8 ? 'success' :
                                  device.backhaul_tx_ratio >= 0.6 ? 'warning' : 'error'
                                }
                                sx={{ 
                                  height: 24,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  cursor: 'help',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            </Tooltip>
                            <Chip
                              size="small"
                              label={
                                device.backhaul_tx_ratio >= 0.8 ? 'Good' :
                                device.backhaul_tx_ratio >= 0.6 ? 'Fair' : 'Poor'
                              }
                              variant="outlined"
                              color={
                                device.backhaul_tx_ratio >= 0.8 ? 'success' :
                                device.backhaul_tx_ratio >= 0.6 ? 'warning' : 'error'
                              }
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      
                      {/* TX Failed Percentage */}
                      {device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: '140px', color: 'text.secondary', fontWeight: 'bold' }}>TX Failed:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Transmission failure percentage - percentage of packets that failed to transmit over the backhaul link. Lower values indicate better link quality.">
                              <Chip
                                size="small"
                                label={`${(device.backhaul_tx_failed_percentage * 100).toFixed(1)}%`}
                                color={
                                  device.backhaul_tx_failed_percentage <= 0.1 ? 'success' :
                                  device.backhaul_tx_failed_percentage <= 0.3 ? 'warning' : 'error'
                                }
                                sx={{ 
                                  height: 24,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  cursor: 'help',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            </Tooltip>
                            <Chip
                              size="small"
                              label={
                                device.backhaul_tx_failed_percentage <= 0.1 ? 'Good' :
                                device.backhaul_tx_failed_percentage <= 0.3 ? 'Fair' : 'Poor'
                              }
                              variant="outlined"
                              color={
                                device.backhaul_tx_failed_percentage <= 0.1 ? 'success' :
                                device.backhaul_tx_failed_percentage <= 0.3 ? 'warning' : 'error'
                              }
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      
                      {/* Latency: median round-trip to parent / CAP, with jitter
                          (max-min) carried in the tooltip. Median is more
                          representative of typical RTT than the average for
                          spiky wireless links. */}
                      {(() => {
                        const rows = [
                          { label: 'Latency to Parent:', summary: device.latency?.to_parent, tooltipLabel: 'Median round-trip to immediate parent' },
                          { label: 'Latency to CAP:', summary: device.latency?.to_cap, tooltipLabel: 'Median round-trip to CAP (root)' },
                        ];
                        return rows.map(({ label, summary, tooltipLabel }) => {
                          const ms = getLatencyValue(summary);
                          if (ms === null) return null;
                          const chipColor = getLatencyChipColor(ms);
                          const qualLabel = getLatencyLabel(ms);
                          return (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
                              <Typography variant="body2" sx={{ minWidth: '140px', color: 'text.secondary', fontWeight: 'bold' }}>{label}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={formatLatencyTooltip(summary, tooltipLabel)}>
                                  <Chip
                                    size="small"
                                    label={formatLatencyMs(ms)}
                                    color={chipColor}
                                    sx={{
                                      height: 24,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      cursor: 'help',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                </Tooltip>
                                <Chip
                                  size="small"
                                  label={qualLabel}
                                  variant="outlined"
                                  color={chipColor}
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                                {summary?.jitter_ms !== undefined && summary?.jitter_ms !== null && (
                                  <Tooltip title={`Jitter (max - min) over ${summary.samples ?? 0} samples`}>
                                    <Chip
                                      size="small"
                                      label={`±${formatLatencyMs(summary.jitter_ms)}`}
                                      variant="outlined"
                                      sx={{
                                        height: 24,
                                        fontSize: '0.7rem',
                                        color: 'text.secondary',
                                        cursor: 'help',
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  ) : null}
                  {device.parent_serial && (() => {
                    const parent = devices.find(d =>
                      d?.serial_number?.toLowerCase?.() === device?.parent_serial?.toLowerCase?.()
                    );
                    const connectionDetails = getConnectionDetails(device, parent);
                    if (!connectionDetails?.rssi && !connectionDetails?.stationRssi) return null;
                    
                    return (
                      <Box sx={{ mt: 2 }}>
                        {connectionDetails.rssi && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>STA → AP RSSI:</Typography>
                            <Chip
                              size="small"
                              label={`${connectionDetails.rssi}dBm`}
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        )}
                        {connectionDetails.stationRssi && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>AP → STA RSSI:</Typography>
                            <Chip
                              size="small"
                              label={`${connectionDetails.stationRssi}dBm`}
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Provisioning Section */}
        {device.provisioning && (() => {
          const prov = device.provisioning;
          const aiMac = prov.AiDeviceMac || prov.ai_device_mac;
          const jnet1Embedded = prov.Jnet1EmbeddedMac || prov.jnet1_embedded_mac;
          const dimmerEmbedded = prov.DimmerEmbeddedSerial || prov.dimmer_embedded_serial;
          const jnet1Nema = prov.Jnet1NemaMac || prov.jnet1_nema_mac;
          const dimmerNema = prov.DimmerNemaSerial || prov.dimmer_nema_serial;

          if (!aiMac && !jnet1Embedded && !dimmerEmbedded && !jnet1Nema && !dimmerNema) {
            return null;
          }

          // Helper function to format jnet1 values: show both decimal and hex
          const formatJnet1 = (value) => {
            if (!value) return value;
            // Check if value is numeric (can be string or number)
            const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
            if (!isNaN(numValue) && isFinite(numValue)) {
              // Convert to hex (uppercase) and pad to even length
              let hex = numValue.toString(16).toUpperCase();
              // Pad with leading zero if odd length
              if (hex.length % 2 !== 0) {
                hex = '0' + hex;
              }
              // Add colons between every 2 digits
              const hexFormatted = hex.match(/.{2}/g).join(':');
              // Return both decimal and hex: "decimal (hex)"
              return `${value} (${hexFormatted})`;
            }
            // If not numeric, return as-is
            return value;
          };

          const row = (label, value) => (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>
                {label}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                {value}
              </Typography>
            </Box>
          );

          return (
            <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Provisioning</Typography>
              <Stack spacing={0.5}>
                {aiMac && row('ai', aiMac)}
                {jnet1Embedded && row('jnet1', formatJnet1(jnet1Embedded))}
                {dimmerEmbedded && row('dimmer', dimmerEmbedded)}
                {jnet1Nema && row('jnet1 (nema)', formatJnet1(jnet1Nema))}
                {dimmerNema && row('dimmer (nema)', dimmerNema)}
              </Stack>
            </Paper>
          );
        })()}

        {/* Connected AI Device - skip for EdgeCore devices and AI devices */}
+        {resolvedAIDevices.length > 0 && !(device?.compatible || '').toLowerCase().includes('edgecore') && !(() => {
          const model = device?.capabilities?.model?.toLowerCase() || '';
          return model.includes('nvidia') || model.includes('qrb5165-128gb');
        })() && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Connected AI Device</Typography>
            <Grid container spacing={2}>
              {resolvedAIDevices.map((aiDevice) => (
                <Grid item xs={12} key={aiDevice.serial_number}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => navigate(`/device/${aiDevice.serial_number.toLowerCase()}`)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Tooltip title={aiDevice.connected ? 'Connected' : 'Disconnected'}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: aiDevice.connected ? '#4caf50' : '#f44336',
                            marginRight: 2,
                          }}
                        />
                      </Tooltip>
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {aiDevice.name || aiDevice.serial_number}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Chip
                          size="small"
                          label={`Firmware: ${aiDevice.firmware || 'N/A'}`}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Last Seen: ${aiDevice.last_seen ? new Date(aiDevice.last_seen).toLocaleString() : 'N/A'}`}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Connected Jnet2 Children Section */}
        {childJnet2Devices.length > 0 && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Tooltip
                title="Children from parent_serial configuration and from this AP's WiFi associations when topology is stale"
                placement="top"
              >
                <Typography variant="h6" sx={{ cursor: 'help' }}>Direct Children</Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  size="small"
                  label={`Total: ${childJnet2Devices.length}`}
                  color="default"
                />
                <Chip
                  size="small"
                  label={`Connected: ${childJnet2Devices.filter(c => c.connected).length}`}
                  color="success"
                />
                <Tooltip title="Based on parent reports" placement="top">
                  <Chip
                    size="small"
                    label={`Active Associations: ${countActiveAssociations(
                      device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces,
                    )}`}
                    color="primary"
                    sx={{ cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
              Total: Configured children (parent_serial) plus devices matched from AP associations • Connected: Children currently online • Active Associations: Real-time WiFi connections on this device
            </Typography>
            <Grid container spacing={2}>
              {childJnet2Devices.map((childDevice) => {
                const connectionDetails = getUplinkDetails(childDevice, device);
                
                return (
                  <Grid item xs={12} key={childDevice.serial_number}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer'
                        },
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}
                      onClick={() => navigate(`/device/${childDevice.serial_number.toLowerCase()}`)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={childDevice.connected ? 'Connected' : 'Disconnected'}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: childDevice.connected ? '#4caf50' : '#f44336',
                                marginRight: 2,
                              }}
                            />
                          </Tooltip>
                          <Typography variant="subtitle1">
                            {childDevice.name || childDevice.serial_number}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`Firmware: ${childDevice.firmware || 'N/A'}`}
                            sx={{ 
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                          <Chip
                            size="small"
                            label={`Last Seen: ${childDevice.last_seen ? new Date(childDevice.last_seen).toLocaleString() : 'N/A'}`}
                            sx={{ 
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        </Box>
                      </Box>
                      {connectionDetails ? (
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 2,
                          mb: 2
                        }}>
                        {/* RX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio RX histograms (parent → child). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.rxRate?.MCS ? ` (MCS ${connectionDetails.rxRate.MCS}` : ''}${connectionDetails.rxRate?.NSS ? `, NSS ${connectionDetails.rxRate.NSS}` : ''}${connectionDetails.rxRate?.BW ? `, BW ${connectionDetails.rxRate.BW} MHz` : ''}${connectionDetails.rxRate?.MCS ? ')' : ''}.${connectionDetails.rxTotalBytes ? ` ${(connectionDetails.rxTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Station (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('child', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.stationRssi ? `${connectionDetails.stationRssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.rxRate?.Bitrate || connectionDetails.rxRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.NSS || connectionDetails.rxRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.MCS || connectionDetails.rxRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>

                        {/* TX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio TX histograms (child → parent). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.txRate?.MCS ? ` (MCS ${connectionDetails.txRate.MCS}` : ''}${connectionDetails.txRate?.NSS ? `, NSS ${connectionDetails.txRate.NSS}` : ''}${connectionDetails.txRate?.BW ? `, BW ${connectionDetails.txRate.BW} MHz` : ''}${connectionDetails.txRate?.MCS ? ')' : ''}.${connectionDetails.txTotalBytes ? ` ${(connectionDetails.txTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Parent AP (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('ap', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rssi ? `${connectionDetails.rssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.txRate?.Bitrate || connectionDetails.txRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.NSS || connectionDetails.txRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.MCS || connectionDetails.txRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      </Box>
                      ) : (
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mb: 2
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            No connection details available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {/* Connected EdgeCore Children Section (from associations on this AP) - ONLY for EdgeCore devices */}
        {childEdgeCoreDevices.length > 0 && device?.compatible?.toLowerCase()?.includes('edgecore') && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Tooltip title="Based on associations reported by this EdgeCore AP" placement="top">
                <Typography variant="h6" sx={{ cursor: 'help' }}>Direct Children (EdgeCore)</Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  size="small"
                  label={`Total: ${childEdgeCoreDevices.length}`}
                  color="default"
                />
                <Chip
                  size="small"
                  label={`Connected: ${childEdgeCoreDevices.length}`}
                  color="success"
                />
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
              EdgeCore children are always connected when present in associations
                        </Typography>
            <Grid container spacing={2}>
              {childEdgeCoreDevices.map((childDevice) => {
                // For EdgeCore children, use association data directly
                const assoc = childDevice.edgeCoreAssoc;
                
                // Format connected time from seconds
                const formatConnectedTime = (seconds) => {
                  if (!seconds || seconds <= 0) return null;
                  if (seconds < 60) return `${seconds}s`;
                  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
                  const hours = Math.floor(seconds / 3600);
                  const mins = Math.floor((seconds % 3600) / 60);
                  if (hours >= 24) {
                    const days = Math.floor(hours / 24);
                    return `${days}d ${hours % 24}h`;
                  }
                  return `${hours}h ${mins}m`;
                };

                // Format bytes
                const formatBytes = (bytes) => {
                  if (!bytes || bytes <= 0) return '0 KB';
                  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
                  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                };

                return (
                  <Grid item xs={12} sm={6} md={4} key={childDevice.serial_number}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer'
                        },
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}
                      onClick={() => {
                        const targetSerial = childDevice.serial_number?.toLowerCase();
                        if (targetSerial) {
                          navigate(`/device/${targetSerial}`);
                        }
                      }}
                    >
                      {/* Header with status and serial */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Tooltip title="Connected">
                        <Box
                          sx={{
                              width: 10,
                              height: 10,
                            borderRadius: '50%',
                              backgroundColor: '#4caf50',
                              mr: 1,
                              flexShrink: 0
                          }}
                        />
                        </Tooltip>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {childDevice.serial_number}
                        </Typography>
                    </Box>
                    
                      {/* Info chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {assoc?.rssi && (
                          <Tooltip title="Signal strength as seen by the EdgeCore AP">
                            <Chip
                              size="small"
                              label={`AP RSSI: ${assoc.rssi} dBm`}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                              color={parseInt(assoc.rssi) >= -70 ? 'success' : parseInt(assoc.rssi) >= -80 ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {formatConnectedTime(assoc?.connectedTime) && (
                          <Tooltip title="Time this device has been connected to the AP">
                            <Chip
                              size="small"
                              label={formatConnectedTime(assoc.connectedTime)}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                              color="info"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {assoc && (assoc.txBytes > 0 || assoc.rxBytes > 0) && (
                          <Chip
                            size="small"
                            label={`↑${formatBytes(assoc.txBytes)} ↓${formatBytes(assoc.rxBytes)}`}
                            sx={{ height: 22, fontSize: '0.7rem' }}
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                  </Paper>
                </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}


        {/* Parent Network Device Section for AI devices and EdgeCore devices */}
        {parentDevice && (
          (() => {
            const modelLower = (device?.capabilities?.model || '').toString().toLowerCase();
            return modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb');
          })() ||
          device?.compatible === 'edgecore_eap102' ||
          device?.capabilities?.compatible === 'edgecore_eap102'
        ) && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Parent Network Device</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      cursor: 'pointer'
                    },
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1
                  }}
                  onClick={() => navigate(`/device/${parentDevice.serial_number.toLowerCase()}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={parentDevice.connected ? 'Connected' : 'Disconnected'}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: parentDevice.connected ? '#4caf50' : '#f44336',
                            marginRight: 2,
                          }}
                        />
                      </Tooltip>
                      <Typography variant="subtitle1">
                        {parentDevice.name || parentDevice.serial_number}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={`Firmware: ${parentDevice.firmware || 'N/A'}`}
                        sx={{ 
                          height: 24,
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                      <Chip
                        size="small"
                        label={`Last Seen: ${parentDevice.last_seen ? new Date(parentDevice.last_seen).toLocaleString() : 'N/A'}`}
                        sx={{ 
                          height: 24,
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  {(() => {
                    const connectionDetails = getUplinkDetails(device, parentDevice);
                    if (!connectionDetails) return null;
                    
                    return (
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                        mb: 2
                      }}>
                        {/* RX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio RX histograms (parent → child). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.rxRate?.MCS ? ` (MCS ${connectionDetails.rxRate.MCS}` : ''}${connectionDetails.rxRate?.NSS ? `, NSS ${connectionDetails.rxRate.NSS}` : ''}${connectionDetails.rxRate?.BW ? `, BW ${connectionDetails.rxRate.BW} MHz` : ''}${connectionDetails.rxRate?.MCS ? ')' : ''}.${connectionDetails.rxTotalBytes ? ` ${(connectionDetails.rxTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Station (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('child', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.stationRssi ? `${connectionDetails.stationRssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.rxRate?.Bitrate || connectionDetails.rxRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.NSS || connectionDetails.rxRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.MCS || connectionDetails.rxRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>

                        {/* TX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio TX histograms (child → parent). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.txRate?.MCS ? ` (MCS ${connectionDetails.txRate.MCS}` : ''}${connectionDetails.txRate?.NSS ? `, NSS ${connectionDetails.txRate.NSS}` : ''}${connectionDetails.txRate?.BW ? `, BW ${connectionDetails.txRate.BW} MHz` : ''}${connectionDetails.txRate?.MCS ? ')' : ''}.${connectionDetails.txTotalBytes ? ` ${(connectionDetails.txTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Parent AP (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('ap', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rssi ? `${connectionDetails.rssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.txRate?.Bitrate || connectionDetails.txRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.NSS || connectionDetails.txRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.MCS || connectionDetails.txRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      </Box>
                    );
                  })()}
                  
                  {/* Association Details for debugging */}
                  {device.wlan_mac && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', mb: 1, display: 'block' }}>
                        Association Details
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        <strong>WLAN MAC:</strong> {device.wlan_mac}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Paper>

      {/* Radio Interfaces Section (omit unused VLAN ifaces with empty mode) */}
      {(radioSectionPrivateIfaces.length > 0 || radioSectionPublicIfaces.length > 0) && (
        <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
          <Tooltip title="Information taken from device report and might take some time to refresh" placement="top">
            <Typography variant="h6" gutterBottom sx={{ cursor: 'help', display: 'inline-block' }}>
              Radio Interfaces
            </Typography>
          </Tooltip>
          <Grid container spacing={2}>
            {/* Private WiFi Interfaces */}
            {radioSectionPrivateIfaces.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1.5,
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    height: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ flexGrow: 1 }}>
                      Private WiFi
                    </Typography>
                    <Chip
                      label={`${radioSectionPrivateIfaces.length} Interface${radioSectionPrivateIfaces.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Stack spacing={1}>
                    {radioSectionPrivateIfaces.map((iface, index) => {
                      // For EdgeCore, Radio.Name might be empty - fallback to first radio
                      const isEdgeCore = device.compatible?.toLowerCase()?.includes('edgecore');
                      const radioName = iface.Radio?.Name || iface.radio?.name || iface.radio?.Ref;
                      let radioInfo = device.radios?.find(r => r.name === radioName);
                      // EdgeCore fallback: if no match, use first radio
                      if (!radioInfo && isEdgeCore && device.radios?.length > 0) {
                        radioInfo = device.radios[0];
                      }
                      // jnet2 fallback: try to match by interface name index (ath1 -> wifi1, ath2 -> wifi2)
                      if (!radioInfo && !isEdgeCore && device.radios?.length > 0) {
                        const ifaceIdx = parseInt((iface.Name || iface.name || '').replace(/\D/g, '')) || (index + 1);
                        radioInfo = device.radios.find(r => {
                          const rIdx = parseInt((r.name || '').replace(/\D/g, ''));
                          return rIdx === ifaceIdx;
                        }) || device.radios[Math.min(index, device.radios.length - 1)];
                      }
                      const isStation = (iface.Mode || iface.mode)?.toLowerCase() === 'station';
                      // Handle both cases for interface properties (EdgeCore uses lowercase)
                      const ifaceName = iface.Name || iface.name;
                      const ifaceSSID = iface.SSID || iface.ssid;
                      const ifaceAssociations = iface.Associations || iface.associations || [];
                      
                      return (
                        <Box 
                          key={`private-${ifaceName || 'iface'}-${index}`}
                          sx={{ 
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                {isEdgeCore ? (ifaceName || `Interface ${index + 1}`) : `Interface ${index + 1}`}
                              </Typography>
                              <Chip
                                label={isStation ? 'Station' : 'AP'}
                                size="small"
                                color={isStation ? 'warning' : 'primary'}
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                              {!isStation && (
                                <Tooltip title="Number of Associated stations">
                                  <Chip
                                    label={ifaceAssociations.length || 0}
                                    size="small"
                                    color="primary"
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.75rem',
                                      ml: 1,
                                      minWidth: '24px'
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            {/* SSID - show for EdgeCore always, for jnet2 only when available */}
                            {(isEdgeCore || ifaceSSID) && (
                            <Tooltip title="SSID" placement="top">
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  bgcolor: 'primary.lighter',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  cursor: 'help'
                                }}
                              >
                                  {ifaceSSID || 'N/A'}
                              </Typography>
                            </Tooltip>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              bgcolor: 'background.default',
                              p: 1,
                              borderRadius: 1,
                              gap: 1
                            }}>
                              {/* EdgeCore-specific display */}
                              {isEdgeCore ? (
                              <Box sx={{ 
                                display: 'grid',
                                  gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 1
                              }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Channel</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ch. Width</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{radioInfo?.channel_width ? `${radioInfo.channel_width} MHz` : 'N/A'}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>TX Power</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{radioInfo?.tx_power ? `${radioInfo.tx_power} dBm` : 'N/A'}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                /* jnet2 display - original format */
                                  <Box sx={{ 
                                  display: 'grid',
                                  gridTemplateColumns: isStation ? 'repeat(7, 1fr)' : 'repeat(5, 1fr)',
                                  gap: 1
                                }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Name</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{ifaceName || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Band</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2">{radioInfo?.band || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Channel</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ch. Width</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2">{radioInfo?.channel_width ? `${radioInfo.channel_width}MHz` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>TX Power</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{iface.TxPower || iface.tx_power ? `${iface.TxPower || iface.tx_power}dBm` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                {isStation && (
                                  <>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>ST RSSI</Typography>
                                        <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                              color: iface.RSSI && parseInt(iface.RSSI) < -80 ? 'error.main' : 'inherit',
                                              fontWeight: iface.RSSI && parseInt(iface.RSSI) < -80 ? 500 : 'normal'
                                          }}
                                        >
                                          <Tooltip title="RSSI measured by station radio (RX)">
                                              <span>{iface.RSSI || iface.rssi ? `${iface.RSSI || iface.rssi}dBm` : 'N/A'}</span>
                                          </Tooltip>
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>AP RSSI</Typography>
                                        <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography 
                                          variant="body2"
                                          sx={{ 
                                            color: (() => {
                                                if (!device.parent_serial) return 'inherit';
                                                const parentDevice = devices.find(d => 
                                                  d.serial_number?.toLowerCase() === device.parent_serial?.toLowerCase()
                                                );
                                                if (!parentDevice) return 'inherit';
                                                const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                                                if (!deviceMacs.length) return 'inherit';
                                                const findTxRssi = (interfaces) => {
                                                  if (!interfaces) return null;
                                                  for (const parentIface of interfaces) {
                                                    const assocs = parentIface.Associations || parentIface.associations || [];
                                                    for (const assoc of assocs) {
                                                      const stationMac = (assoc.Station || assoc.station || '').toLowerCase();
                                                      if (deviceMacs.includes(stationMac)) {
                                                        return parseInt(assoc.RSSI || assoc.rssi);
                                                      }
                                                    }
                                                  }
                                                  return null;
                                                };
                                                const apRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                                              findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);
                                              return apRssi && apRssi < -80 ? 'error.main' : 'inherit';
                                            })(),
                                            fontWeight: (() => {
                                                if (!device.parent_serial) return 'normal';
                                                const parentDevice = devices.find(d => 
                                                  d.serial_number?.toLowerCase() === device.parent_serial?.toLowerCase()
                                                );
                                                if (!parentDevice) return 'normal';
                                                const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                                                if (!deviceMacs.length) return 'normal';
                                                const findTxRssi = (interfaces) => {
                                                  if (!interfaces) return null;
                                                  for (const parentIface of interfaces) {
                                                    const assocs = parentIface.Associations || parentIface.associations || [];
                                                    for (const assoc of assocs) {
                                                      const stationMac = (assoc.Station || assoc.station || '').toLowerCase();
                                                      if (deviceMacs.includes(stationMac)) {
                                                        return parseInt(assoc.RSSI || assoc.rssi);
                                                      }
                                                    }
                                                  }
                                                  return null;
                                                };
                                                const apRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                                              findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);
                                              return apRssi && apRssi < -80 ? 500 : 'normal';
                                            })()
                                          }}
                                        >
                                          <Tooltip title="RSSI as seen by parent AP (TX)">
                                            <span>
                                              {(() => {
                                                if (!device.parent_serial) return 'N/A';
                                                const parentDevice = devices.find(d => 
                                                    d.serial_number?.toLowerCase() === device.parent_serial?.toLowerCase()
                                                );
                                                if (!parentDevice) return 'N/A';
                                                  const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                                                  if (!deviceMacs.length) return 'N/A';
                                                const findTxRssi = (interfaces) => {
                                                  if (!interfaces) return null;
                                                  for (const parentIface of interfaces) {
                                                      const assocs = parentIface.Associations || parentIface.associations || [];
                                                      for (const assoc of assocs) {
                                                        const stationMac = (assoc.Station || assoc.station || '').toLowerCase();
                                                      if (deviceMacs.includes(stationMac)) {
                                                          return assoc.RSSI || assoc.rssi;
                                                      }
                                                    }
                                                  }
                                                  return null;
                                                };
                                                  const apRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                                                findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);
                                                return apRssi ? `${apRssi}dBm` : 'N/A';
                                              })()}
                                            </span>
                                          </Tooltip>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </>
                                )}
                              </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            )}

            {/* Public WiFi Interfaces */}
            {radioSectionPublicIfaces.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1.5,
                    borderLeft: '4px solid',
                    borderColor: 'info.main',
                    height: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="info.main" sx={{ flexGrow: 1 }}>
                      Public WiFi
                    </Typography>
                    <Chip
                      label={`${radioSectionPublicIfaces.length} Interface${radioSectionPublicIfaces.length > 1 ? 's' : ''}`}
                      size="small"
                      color="info"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Stack spacing={1}>
                    {radioSectionPublicIfaces.map((iface, index) => {
                      // Handle both cases for interface properties (jnet2 uses lowercase)
                      const radioName = iface.Radio?.Name || iface.radio?.name || iface.radio?.Name;
                      const radioInfo = device.radios?.find(r => r.name === radioName);
                      const isStation = (iface.Mode || iface.mode)?.toLowerCase() === 'station';
                      const ifaceName = iface.Name || iface.name;
                      const ifaceSSID = iface.SSID || iface.ssid;
                      const assocCount = !isStation
                        ? publicWifiClientCountForInterface(iface, device?.public_wifi, radioInfo?.band)
                        : 0;
                      
                      return (
                        <Box 
                          key={`public-${ifaceName || 'iface'}-${index}`}
                          sx={{ 
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                Interface {index + 1}
                              </Typography>
                              <Chip
                                label={isStation ? 'Station' : 'AP'}
                                size="small"
                                color={isStation ? 'warning' : 'info'}
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                              {!isStation && (
                                <Tooltip title="Number of associated stations">
                                  <Chip
                                    label={assocCount}
                                    size="small"
                                    color="info"
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.75rem',
                                      ml: 1,
                                      minWidth: '24px'
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            <Tooltip title="SSID" placement="top">
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  color: 'info.main',
                                  fontWeight: 500,
                                  bgcolor: 'info.lighter',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  cursor: 'help'
                                }}
                              >
                                {ifaceSSID || 'N/A'}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              bgcolor: 'background.default',
                              p: 1,
                              borderRadius: 1,
                              gap: 1
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: isStation ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)',
                                gap: 1
                              }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Name</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{ifaceName || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Band</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{radioInfo?.band || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Channel</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ch. Width</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{radioInfo?.channel_width ? `${radioInfo.channel_width}MHz` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>TX Power</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{(iface.TxPower || iface.tx_power) ? `${iface.TxPower || iface.tx_power}dBm` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                {isStation && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>RSSI</Typography>
                                    <Box sx={{ 
                                      bgcolor: 'background.paper',
                                      p: 0.75,
                                      borderRadius: 0.5,
                                      border: '1px solid',
                                      borderColor: 'divider'
                                    }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          color: (() => {
                                            const rssi = parseInt(iface.RSSI || iface.rssi);
                                            return rssi && rssi < -80 ? 'error.main' : 'inherit';
                                          })(),
                                          fontWeight: (() => {
                                            const rssi = parseInt(iface.RSSI || iface.rssi);
                                            return rssi && rssi < -80 ? 500 : 'normal';
                                          })()
                                        }}
                                      >
                                        <Tooltip title="RSSI measure by station radio">
                                          <span>{(iface.RSSI || iface.rssi) ? `${iface.RSSI || iface.rssi}dBm` : 'N/A'}</span>
                                        </Tooltip>
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Device Configuration</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSwitchConfigMode(configMode === 'fields' ? 'raw' : 'fields')}
              >
                {configMode === 'fields' ? 'Switch to Raw JSON' : 'Switch to Fields'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {configMode === 'raw' && (
              <Tooltip title="Format JSON">
                <IconButton 
                  onClick={handleFormatJson} 
                  color="primary" 
                  size="small"
                  disabled={configLoading}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <FormatIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              )}
              {configMode === 'raw' && (
              <Tooltip title="Reset Changes">
                <IconButton 
                  onClick={handleReset} 
                  color="secondary" 
                  size="small"
                  disabled={configLoading}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ResetIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              )}
              <Tooltip title="Refresh">
                <IconButton 
                  onClick={configMode === 'fields' ? fetchTreeMgmtView : fetchDeviceConfig}
                  color="primary"
                  size="small"
                  disabled={configMode === 'fields' ? treeMgmtLoading : configLoading}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              {device && (device.capabilities?.model === 'JuganuNode' || device.capabilities?.model === 'JuganuCAP') && (
                <Tooltip
                  title={
                    <Box sx={{ p: 1, maxWidth: 400 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '0.9rem' }}>
                        📋 Configuration Help for {device.capabilities?.model} - 6GHz to 5GHz Switch
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                            📡 Radio Configuration
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Set <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"band": 2</code>
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            (1=2.4GHz, 2=5GHz, 3=6GHz)
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                            🌳 TreeMgmt Configuration
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>
                            1. Set <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"band": "5GHz"</code>
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>
                            2. Replace <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"ChannelGroups"</code> with <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"ChannelGroupIDs"</code>
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            Use [1, 2] for 5GHz, [3, 4, 5, 6] for 6GHz
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1, bgcolor: 'grey.900', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem', color: 'grey.300' }}>
                            Example 5GHz Configuration:
                          </Typography>
                          <code style={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.3, whiteSpace: 'pre-wrap', color: 'white' }}>
                            {`"radio": [{"band": "2", "radio": "5GHz_0"}],\n"treemgmt": [{"band": "5GHz", "ChannelGroupIDs": [1, 2]}]`}
                          </code>
                        </Box>
                      </Box>
                    </Box>
                  }
                  placement="bottom-start"
                  arrow
                  PopperProps={{
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 3,
                        fontSize: '0.75rem',
                        maxWidth: 'none',
                        p: 0,
                      },
                      '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                      }
                    }
                  }}
                >
                  <IconButton 
                    size="small" 
                    sx={{ 
                      color: 'info.main',
                      width: 32, 
                      height: 32,
                      borderRadius: 1,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <HelpIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {configMode === 'raw' && configError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {configError}
            </Alert>
          )}
          {configMode === 'fields' && treeMgmtError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {treeMgmtError}
            </Alert>
          )}
          {saveStatus.message && (
            <Alert severity={saveStatus.type} sx={{ mb: 2 }}>
              {saveStatus.message}
            </Alert>
          )}

          {configMode === 'fields' ? (
            treeMgmtLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : !treeMgmtView ? (
              <Alert severity="info">No tree-management configuration available.</Alert>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Field updates only modify the tree-management portion of the device
                  config. Public WiFi settings (SSIDs / radio profiles) are preserved.
                </Alert>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip
                    title={
                      canAddTree()
                        ? 'Add a new treemgmt entry'
                        : `${treeMgmtView.device_type === 'cap' ? 'CAP' : 'Non-CAP'} devices may have at most ${maxAllowedTrees()} treemgmt entr${maxAllowedTrees() === 1 ? 'y' : 'ies'}`
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={openAddTreeDialog}
                        disabled={!canAddTree() || availableTreeIDs().length === 0}
                      >
                        Add Tree
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                {/* Client Radio section — only for non-CAP (Node) devices.
                    tx_power_up controls the STA radio that connects upward
                    to the parent; not meaningful on CAP (root) devices. */}
                {treeMgmtView.device_type !== 'cap' && (treeMgmtView.trees || []).length > 0 && (() => {
                  const firstTree = (treeMgmtView.trees || [])[0];
                  return (
                    <Paper variant="outlined" sx={fieldSectionSx}>
                      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Client Radio
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Transmit power for the radio that connects this device upward to its parent.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TextField
                          size="small"
                          label="TX Power Up (dBm)"
                          type="number"
                          sx={{ width: 160 }}
                          value={treeFieldValue(firstTree, 'tx_power_up')}
                          onChange={(e) => setTreeEdit(firstTree.tree_id, 'tx_power_up', e.target.value)}
                        />
                      </Box>
                    </Paper>
                  );
                })()}

                {(treeMgmtView.trees || []).length === 0 && (
                  <Alert severity="warning">This device has no treemgmt entries.</Alert>
                )}
                {(treeMgmtView.trees || []).map((tree) => {
                  const mode = channelModeForTree(tree);
                  return (
                  <Paper key={tree.tree_id} variant="outlined" sx={fieldSectionSx}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                      Tree <code>{tree.tree_id}</code>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Band</InputLabel>
                        <Select
                          label="Band"
                          value={treeFieldValue(tree, 'band')}
                          onChange={(e) => {
                            const newBand = e.target.value;
                            setTreeEdit(tree.tree_id, 'band', newBand);
                            // Drop any pending UNII ids that don't belong to the
                            // newly chosen band so the user doesn't accidentally
                            // submit a cross-band selection.
                            const currentIds = parseChannelGroupIds(channelGroupIdsValue(tree));
                            const filtered = currentIds.filter(id => UNII_BY_ID[id]?.band === newBand);
                            if (filtered.length !== currentIds.length) {
                              setTreeEdit(tree.tree_id, 'channel_group_ids', filtered.join(', '));
                            }
                          }}
                        >
                          <MenuItem value="5GHz">5GHz</MenuItem>
                          <MenuItem value="6GHz">6GHz</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>HT Mode</InputLabel>
                        <Select
                          label="HT Mode"
                          value={treeFieldValue(tree, 'htmode')}
                          onChange={(e) => setTreeEdit(tree.tree_id, 'htmode', e.target.value)}
                        >
                          <MenuItem value="HT20">HT20</MenuItem>
                          <MenuItem value="HT40">HT40</MenuItem>
                          <MenuItem value="HT80">HT80</MenuItem>
                          <MenuItem value="HT160">HT160</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="TX Power Down (dBm)"
                        type="number"
                        sx={{ width: 170 }}
                        value={treeFieldValue(tree, 'tx_power_down')}
                        onChange={(e) => setTreeEdit(tree.tree_id, 'tx_power_down', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Max Children"
                        type="number"
                        sx={{ width: 140 }}
                        value={treeFieldValue(tree, 'max_children')}
                        onChange={(e) => setTreeEdit(tree.tree_id, 'max_children', e.target.value)}
                      />
                    </Box>

                    <Divider sx={{ my: 1.5 }}>
                      <Chip label="Channel selection" size="small" />
                    </Divider>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                      <Box>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={mode}
                          onChange={(_, v) => v && setTreeEdit(tree.tree_id, 'channel_mode', v)}
                        >
                          <ToggleButton value="single">Single Channel</ToggleButton>
                          <ToggleButton value="group_ids">Channel Group IDs</ToggleButton>
                          <ToggleButton value="groups">Raw Channel Groups</ToggleButton>
                        </ToggleButtonGroup>
                        <FormHelperText sx={{ mt: 0.5 }}>
                          {mode === 'single' && 'Pin the radio to a specific channel (0 = device picks automatically).'}
                          {mode === 'group_ids' && 'Pick one or more U-NII sub-bands. Device auto-picks the best channel; fixed channel is reset to 0.'}
                          {mode === 'groups' && 'Provide an explicit list of channels per band. Device auto-picks the best one; fixed channel is reset to 0.'}
                        </FormHelperText>
                      </Box>
                      {mode === 'single' && (
                        <TextField
                          size="small"
                          label="Channel"
                          type="number"
                          sx={{ width: 140 }}
                          value={treeFieldValue(tree, 'channel')}
                          onChange={(e) => setTreeEdit(tree.tree_id, 'channel', e.target.value)}
                        />
                      )}
                      {mode === 'group_ids' && (() => {
                        const currentBand = treeFieldValue(tree, 'band');
                        const allIds = parseChannelGroupIds(channelGroupIdsValue(tree));
                        // Hide cross-band IDs: if the band picker still shows
                        // an id from the other band (e.g. legacy data), surface
                        // a warning rather than silently dropping it.
                        const inBandIds = allIds.filter(id => UNII_BY_ID[id]?.band === currentBand);
                        const offBandIds = allIds.filter(id => UNII_BY_ID[id]?.band && UNII_BY_ID[id].band !== currentBand);
                        const allowedOptions = UNII_GROUPS.filter(g => g.band === currentBand);
                        const selectedOptions = allowedOptions.filter(g => inBandIds.includes(g.id));
                        const summary = summarizeUniiSelection(inBandIds);
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 360, flexGrow: 1 }}>
                            <Autocomplete
                              multiple
                              size="small"
                              disableCloseOnSelect
                              options={allowedOptions}
                              value={selectedOptions}
                              isOptionEqualToValue={(opt, val) => opt.id === val.id}
                              getOptionLabel={(opt) => formatUniiOption(opt)}
                              onChange={(_, v) => setTreeEdit(
                                tree.tree_id,
                                'channel_group_ids',
                                v.map(o => o.id).sort((a, b) => a - b).join(', '),
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((opt, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={opt.id}
                                    size="small"
                                    label={`${opt.id} · ${opt.label}`}
                                    sx={{ height: 22 }}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`Channel Groups (${currentBand} U-NII sub-bands)`}
                                  placeholder={selectedOptions.length === 0 ? `Pick one or more ${currentBand} U-NII sub-bands...` : ''}
                                />
                              )}
                            />
                            {summary && (
                              <Alert severity="info" variant="outlined" sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                {summary}
                              </Alert>
                            )}
                            {offBandIds.length > 0 && (
                              <Alert
                                severity="warning"
                                variant="outlined"
                                sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}
                                action={
                                  <Button
                                    size="small"
                                    onClick={() => setTreeEdit(
                                      tree.tree_id,
                                      'channel_group_ids',
                                      inBandIds.join(', '),
                                    )}
                                  >
                                    Drop them
                                  </Button>
                                }
                              >
                                {`Currently saved off-band IDs ${offBandIds.join(', ')} don't match band ${currentBand} and will be ignored.`}
                              </Alert>
                            )}
                          </Box>
                        );
                      })()}
                      {mode === 'groups' && (() => {
                        const currentBand = treeFieldValue(tree, 'band');
                        const is5G = currentBand === '5GHz';
                        const is6G = currentBand === '6GHz';
                        return (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                              size="small"
                              label="5GHz channels"
                              sx={{ width: 220 }}
                              placeholder="e.g. 36, 40, 44"
                              disabled={!is5G}
                              helperText={!is5G ? 'Not available on this band' : ''}
                              value={channelGroupsValueForBand(tree, '5GHz')}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'channel_groups_5GHz', e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="6GHz channels"
                              sx={{ width: 220 }}
                              placeholder="e.g. 1, 5, 9"
                              disabled={!is6G}
                              helperText={!is6G ? 'Not available on this band' : ''}
                              value={channelGroupsValueForBand(tree, '6GHz')}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'channel_groups_6GHz', e.target.value)}
                            />
                          </Box>
                        );
                      })()}
                    </Box>

                    {treeMgmtView.device_type !== 'cap' && (
                    <Accordion sx={{ mt: 2 }} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">Background STA scan</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id={`bg-scan-on-${tree.tree_id}`} shrink>BG Scan</InputLabel>
                            <Select
                              labelId={`bg-scan-on-${tree.tree_id}`}
                              label="BG Scan"
                              value={bgFieldValue(tree, 'bg_scan_on_off') || ''}
                              displayEmpty
                              renderValue={bgScanSelectRenderValue}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'bg_bg_scan_on_off', e.target.value)}
                            >
                              <MenuItem value=""><em>(unchanged)</em></MenuItem>
                              <MenuItem value="on">on</MenuItem>
                              <MenuItem value="off">off</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 200 }}
                            label={(
                              <FieldLabelTip title="How long to wait between scanning one channel and the other (idle time between two channel scanning).">
                                Channel scan interval (s)
                              </FieldLabelTip>
                            )}
                            InputLabelProps={{ shrink: true }}
                            value={bgFieldValue(tree, 'chan_scan_interval')}
                            onChange={(e) => setTreeEdit(tree.tree_id, 'bg_chan_scan_interval', e.target.value)}
                          />
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id={`bg-fr-${tree.tree_id}`} shrink>Fast recovery</InputLabel>
                            <Select
                              labelId={`bg-fr-${tree.tree_id}`}
                              label="Fast recovery"
                              value={bgFieldValue(tree, 'fr_on_off') || ''}
                              displayEmpty
                              renderValue={bgScanSelectRenderValue}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'bg_fr_on_off', e.target.value)}
                            >
                              <MenuItem value=""><em>(unchanged)</em></MenuItem>
                              <MenuItem value="on">on</MenuItem>
                              <MenuItem value="off">off</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 180 }}
                            label={(
                              <FieldLabelTip title="How often to run the full bg scan">
                                Scan interval (s)
                              </FieldLabelTip>
                            )}
                            InputLabelProps={{ shrink: true }}
                            value={bgFieldValue(tree, 'scan_interval')}
                            onChange={(e) => setTreeEdit(tree.tree_id, 'bg_scan_interval', e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Self improvement"
                            sx={{ width: 180 }}
                            value={bgSelfImprovementDisplay(tree)}
                            onChange={(e) => setTreeEdit(
                              tree.tree_id,
                              'bg_self_improvment',
                              normalizeBgSelfImprovementInput(e.target.value),
                            )}
                          />
                        </Box>
                        {(() => {
                          const currentBand = treeFieldValue(tree, 'band');
                          const is5G = currentBand === '5GHz';
                          const is6G = currentBand === '6GHz';
                          return (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                              <TextField
                                size="small"
                                label="ChannelGroups (5GHz)"
                                sx={{ width: 240 }}
                                value={bgChannelGroupsDisplayForBand(tree, '5GHz')}
                                InputProps={{ readOnly: true }}
                                disabled={!is5G}
                                helperText={!is5G ? 'Not available on this band' : 'background_sta_scan scan targets'}
                              />
                              <TextField
                                size="small"
                                label="ChannelGroups (6GHz)"
                                sx={{ width: 240 }}
                                value={bgChannelGroupsDisplayForBand(tree, '6GHz')}
                                InputProps={{ readOnly: true }}
                                disabled={!is6G}
                                helperText={!is6G ? 'Not available on this band' : 'background_sta_scan scan targets'}
                              />
                            </Box>
                          );
                        })()}
                      </AccordionDetails>
                    </Accordion>
                    )}
                  </Paper>
                  );
                })}

                {/* Port Forwarding — jnet2 network boards only. Lets the
                    operator manage the port-forward subset of `app[]`
                    independently of the tree-management edits above. */}
                <PortForwardingSection
                  serial={serial}
                  isJnet2={device?.capabilities?.compatible === 'jnet2'}
                />

                <SyslogSection
                  serial={serial}
                  isJnet2={device?.capabilities?.compatible === 'jnet2'}
                />

              </Box>
            )
          ) : (
            configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Raw mode overwrites the entire device configuration, including the
                  Public WiFi <code>ap[]</code> section. Use the Fields view for
                  tree-management changes that should preserve Public WiFi.
                </Alert>
                <Box sx={{ height: '500px', border: '1px solid #ccc', borderRadius: 1, mb: 2 }}>
                  <Editor
                    height="100%"
                    defaultLanguage="json"
                    value={configJson}
                    onChange={handleEditorChange}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      wordWrap: 'on',
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                    }}
                  />
                </Box>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          {configMode === 'fields' ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setTreeMgmtEdits({})}
                startIcon={<ResetIcon />}
                disabled={treeMgmtLoading || Object.keys(treeMgmtEdits).length === 0}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTreeMgmt}
                disabled={treeMgmtLoading || Object.keys(treeMgmtEdits).length === 0}
                startIcon={<SaveIcon />}
              >
                Apply Tree-Mgmt Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<ResetIcon />}
                disabled={configLoading}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveConfig}
                disabled={!isJsonValid || configLoading}
                startIcon={<SaveIcon />}
              >
                Save Configuration
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Tree-Mgmt Entry Dialog */}
      <Dialog open={addTreeOpen} onClose={() => setAddTreeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tree Management Entry</DialogTitle>
        <DialogContent>
          {addTreeStatus.message && (
            <Alert severity={addTreeStatus.type || 'info'} sx={{ mb: 2 }}>
              {addTreeStatus.message}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            tree_id must match an existing radio name on this device that isn't already
            referenced by another treemgmt entry.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>tree_id</InputLabel>
              <Select
                label="tree_id"
                value={addTreeDraft.tree_id}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, tree_id: e.target.value }))}
              >
                {availableTreeIDs().length === 0 && (
                  <MenuItem value="" disabled>
                    <em>No available radios</em>
                  </MenuItem>
                )}
                {availableTreeIDs().map(rid => (
                  <MenuItem key={rid} value={rid}>{rid}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Band</InputLabel>
              <Select
                label="Band"
                value={addTreeDraft.band}
                onChange={(e) => setAddTreeDraft(d => {
                  const newBand = e.target.value;
                  // Drop any drafted UNII ids and the off-band raw channels so
                  // the user can't accidentally submit a cross-band entry.
                  const offBand = newBand === '5GHz' ? '6GHz' : '5GHz';
                  return {
                    ...d,
                    band: newBand,
                    channel_group_ids: d.channel_group_ids.filter(id => UNII_BY_ID[id]?.band === newBand),
                    [`channel_groups_${offBand}`]: '',
                  };
                })}
              >
                <MenuItem value="5GHz">5GHz</MenuItem>
                <MenuItem value="6GHz">6GHz</MenuItem>
              </Select>
            </FormControl>
            <Divider>
              <Chip label="Channel selection" size="small" />
            </Divider>
            <Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={addTreeDraft.channel_mode}
                onChange={(_, v) => v && setAddTreeDraft(d => ({ ...d, channel_mode: v }))}
              >
                <ToggleButton value="single">Single Channel</ToggleButton>
                <ToggleButton value="group_ids">Channel Group IDs</ToggleButton>
                <ToggleButton value="groups">Raw Channel Groups</ToggleButton>
              </ToggleButtonGroup>
              <FormHelperText sx={{ mt: 0.5 }}>
                {addTreeDraft.channel_mode === 'single' && 'Pin the radio to a specific channel (0 = device picks automatically).'}
                {addTreeDraft.channel_mode === 'group_ids' && 'Pick one or more U-NII sub-bands. Device auto-picks the best channel; fixed channel is left at 0.'}
                {addTreeDraft.channel_mode === 'groups' && 'Provide an explicit list of channels per band. Device auto-picks the best one; fixed channel is left at 0.'}
              </FormHelperText>
            </Box>
            {addTreeDraft.channel_mode === 'single' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Channel"
                  type="number"
                  fullWidth
                  value={addTreeDraft.channel}
                  onChange={(e) => setAddTreeDraft(d => ({ ...d, channel: e.target.value }))}
                />
              </Box>
            )}
            {addTreeDraft.channel_mode === 'group_ids' && (() => {
              const allowedOptions = UNII_GROUPS.filter(g => g.band === addTreeDraft.band);
              const selectedOptions = allowedOptions.filter(g => addTreeDraft.channel_group_ids.includes(g.id));
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Autocomplete
                    multiple
                    size="small"
                    disableCloseOnSelect
                    options={allowedOptions}
                    value={selectedOptions}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    getOptionLabel={(opt) => formatUniiOption(opt)}
                    onChange={(_, v) => setAddTreeDraft(d => ({
                      ...d,
                      channel_group_ids: v.map(o => o.id).sort((a, b) => a - b),
                    }))}
                    renderTags={(value, getTagProps) =>
                      value.map((opt, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={opt.id}
                          size="small"
                          label={`${opt.id} · ${opt.label}`}
                          sx={{ height: 22 }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Channel Groups (${addTreeDraft.band} U-NII sub-bands)`}
                        placeholder={addTreeDraft.channel_group_ids.length === 0 ? `Pick one or more ${addTreeDraft.band} U-NII sub-bands...` : ''}
                      />
                    )}
                  />
                  {summarizeUniiSelection(addTreeDraft.channel_group_ids) && (
                    <Alert severity="info" variant="outlined" sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                      {summarizeUniiSelection(addTreeDraft.channel_group_ids)}
                    </Alert>
                  )}
                </Box>
              );
            })()}
            {addTreeDraft.channel_mode === 'groups' && (() => {
              const is5G = addTreeDraft.band === '5GHz';
              const is6G = addTreeDraft.band === '6GHz';
              return (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="5GHz channels"
                    fullWidth
                    placeholder="e.g. 36, 40, 44"
                    disabled={!is5G}
                    helperText={!is5G ? 'Not available on this band' : ''}
                    value={addTreeDraft.channel_groups_5GHz}
                    onChange={(e) => setAddTreeDraft(d => ({ ...d, channel_groups_5GHz: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="6GHz channels"
                    fullWidth
                    placeholder="e.g. 1, 5, 9"
                    disabled={!is6G}
                    helperText={!is6G ? 'Not available on this band' : ''}
                    value={addTreeDraft.channel_groups_6GHz}
                    onChange={(e) => setAddTreeDraft(d => ({ ...d, channel_groups_6GHz: e.target.value }))}
                  />
                </Box>
              );
            })()}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>HT Mode</InputLabel>
                <Select
                  label="HT Mode"
                  value={addTreeDraft.htmode}
                  onChange={(e) => setAddTreeDraft(d => ({ ...d, htmode: e.target.value }))}
                >
                  <MenuItem value=""><em>(default)</em></MenuItem>
                  <MenuItem value="HT20">HT20</MenuItem>
                  <MenuItem value="HT40">HT40</MenuItem>
                  <MenuItem value="HT80">HT80</MenuItem>
                  <MenuItem value="HT160">HT160</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label="TX Power Up"
                type="number"
                fullWidth
                value={addTreeDraft.tx_power_up}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, tx_power_up: e.target.value }))}
              />
              <TextField
                size="small"
                label="TX Power Down"
                type="number"
                fullWidth
                value={addTreeDraft.tx_power_down}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, tx_power_down: e.target.value }))}
              />
              <TextField
                size="small"
                label="Max Children"
                type="number"
                fullWidth
                value={addTreeDraft.max_children}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, max_children: e.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTreeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTreeSubmit}
            disabled={!addTreeDraft.tree_id}
          >
            Add Tree
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parent Selection Dialog */}
      <Dialog open={parentDialogOpen} onClose={handleCloseParentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Set Parent Device</DialogTitle>
        <DialogContent>
          {parentUpdateStatus.message && (
            <Alert severity={parentUpdateStatus.type} sx={{ mb: 2 }}>
              {parentUpdateStatus.message}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Parent Device</InputLabel>
            <Select
              value={selectedParent}
              onChange={(e) => setSelectedParent(e.target.value)}
              label="Select Parent Device"
              MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {renderAccessListSelectFilter(parentDeviceFilter, setParentDeviceFilter)}
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {filteredParentDevices.map((dev) => (
                <MenuItem key={dev.serial_number} value={dev.serial_number}>
                  {dev.name || dev.serial_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParentDialog}>Cancel</Button>
          <Button 
            onClick={handleSetParent} 
            variant="contained" 
            color="primary"
            disabled={selectedParent.length === 0}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Access Lists Management Dialog */}
      <Dialog open={accessListDialogOpen} onClose={handleCloseAccessListDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Access Lists
        </DialogTitle>
        <DialogContent>
          {accessListUpdateStatus.message && (
            <Alert severity={accessListUpdateStatus.type} sx={{ mb: 2 }}>
              {accessListUpdateStatus.message}
            </Alert>
          )}
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Whitelist insist time (seconds)"
                type="number"
                fullWidth
                size="small"
                value={accessListInsistSec}
                onChange={(e) => setAccessListInsistSec(e.target.value)}
                inputProps={{ min: 1, max: 600, step: 1 }}
                helperText="Optional. 1–600 seconds; firmware hold time for the whitelist. Leave empty to keep the current value. Sent before access-list changes."
              />
            </Grid>
            {/* Whitelist Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
                Whitelist (Allowed Devices)
              </Typography>
              <FormControl fullWidth size="medium">
                <InputLabel id="whitelist-label">Select Whitelisted Devices</InputLabel>
                <Select
                  labelId="whitelist-label"
                  multiple
                  value={selectedWhitelist}
                  onChange={handleWhitelistChange}
                  label="Select Whitelisted Devices"
                  MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
                  sx={{
                    minHeight: '56px',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      padding: '8px 14px',
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No devices selected
                        </Typography>
                      ) : (
                        selected.map((value) => {
                          const device = availableDevices.find(d => d.serial_number === value);
                          return (
                            <Chip
                              key={value}
                              label={device?.name || value}
                              color="success"
                              size="small"
                              onDelete={() => {
                                setSelectedWhitelist(prev => prev.filter(id => id !== value));
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          );
                        })
                      )}
                    </Box>
                  )}
                >
                  {renderAccessListSelectFilter(whitelistDeviceFilter, setWhitelistDeviceFilter)}
                  {filteredWhitelistDevices.map((device) => (
                    <MenuItem key={device.serial_number} value={device.serial_number}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: device.connected ? '#4caf50' : '#f44336',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" noWrap>
                          {device.name || device.serial_number}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Blacklist Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'error.main', mb: 2 }}>
                Blacklist (Blocked Devices)
              </Typography>
              <FormControl fullWidth size="medium">
                <InputLabel id="blacklist-label">Select Blacklisted Devices</InputLabel>
                <Select
                  labelId="blacklist-label"
                  multiple
                  value={selectedBlacklist}
                  onChange={handleBlacklistChange}
                  label="Select Blacklisted Devices"
                  MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
                  sx={{
                    minHeight: '56px',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      padding: '8px 14px',
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No devices selected
                        </Typography>
                      ) : (
                        selected.map((value) => {
                          const device = availableDevices.find(d => d.serial_number === value);
                          return (
                            <Chip
                              key={value}
                              label={device?.name || value}
                              color="error"
                              size="small"
                              onDelete={() => {
                                setSelectedBlacklist(prev => prev.filter(id => id !== value));
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          );
                        })
                      )}
                    </Box>
                  )}
                >
                  {renderAccessListSelectFilter(blacklistDeviceFilter, setBlacklistDeviceFilter)}
                  {filteredBlacklistDevices.map((device) => (
                    <MenuItem key={device.serial_number} value={device.serial_number}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: device.connected ? '#4caf50' : '#f44336',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" noWrap>
                          {device.name || device.serial_number}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ Important: Settings are applied immediately but may take up to 2 minutes to refresh in the interface.
            </Typography>
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
            <Typography variant="body2" color="info.main">
              <strong>Note:</strong> Whitelist allows only the selected devices to connect to this device's AP. 
              Blacklist prevents the selected devices from connecting. If a device is in both lists, 
              the blacklist takes precedence.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAccessListDialog}>Cancel</Button>
          <Button 
            onClick={handleSetAccessList} 
            variant="contained" 
            color="primary"
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Command Dialog */}
      <Dialog open={commandDialogOpen} onClose={handleCloseCommandDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Command to Device</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Command"
            type="text"
            fullWidth
            variant="outlined"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            multiline
            rows={4}
          />
          {commandStatus.message && (
            <Alert severity={commandStatus.type} sx={{ mt: 2 }}>
              {commandStatus.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommandDialog}>Cancel</Button>
          <Button 
            onClick={handleSendCommand} 
            variant="contained" 
            color="primary"
            disabled={!command.trim()}
          >
            Send Command
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default DeviceDetails; 